// Statements — upload workday (console-personas.md §4.4) + R2 confirm cards
// (docs/design/statement-reconciliation.md). Upload → "processing…" → confirm.
// Reuses to-do/confirm patterns; no parallel notification system.

import { AdminApiError, type AdminClient } from "../api.js";
import { failureCopy } from "../ui/failure.js";
import { mountChartChooser, requestSlot } from "../chart_chooser.js";
import {
  chartLoadFromError,
  chartLoadFromSnapshot,
  type ChartLoadState,
} from "../chart_load.js";
import { clear, h } from "../dom.js";
import {
  listSessionUploads,
  recordSessionUpload,
  type SessionUpload,
} from "../session-uploads.js";
import {
  MAX_STATEMENT_PDF_BYTES,
  type AccountPath,
  type CompanySlug,
  type StaffRole,
  type StatementConfirmRequest,
  type StatementReconRow,
} from "../types.js";
import { settleReads } from "../read-cache.js";
import { button, disclosure, pageHeader } from "../ui/components.js";
import { createFreshnessCaption, formatFreshness } from "../ui/freshness.js";
import {
  accountLabel,
  accountLabelBeside,
  dateText,
  isBetweenCompaniesAccount,
  moneyText,
  monthText,
  type AccountLabelContext,
} from "../ui/display-words.js";
import { decimalToCents } from "../ui/money.js";
import { readerRole } from "../ui/facts.js";
import { advice } from "../ui/capabilities.js";
import { navLabel, sentence } from "../ui/sentences.js";
import { navigateTo } from "../ui/nav-bus.js";
import {
  createCompanyDirectory,
  type CompanyDirectory,
  type CompanyDirectoryRow,
} from "../ui/face.js";
import * as face from "../ui/face.js";
import { runWrite } from "../ui/write.js";
import { accountText } from "../console/report-render.js";
import {
  createPeriodControl,
  formatPeriodLabel,
  isValidPeriod,
  periodNotAfterUtc,
} from "../ui/period-control.js";

const LIMIT_MB = Math.floor(MAX_STATEMENT_PDF_BYTES / (1024 * 1024));
const ACCOUNT_PATTERN = /^[A-Za-z][A-Za-z0-9:-]*$/;
const MASK_PATTERN = /^\d{4}$/;
const POLL_MS = 5_000;
const POLL_MAX_MS = 90_000;

/**
 * Two different states used to share one sentence, and it was false for the
 * common one (rd Z8 round 2, MUST-FIX 1). Every upload nulls the reconciliation
 * binding and sets `needs_review` until the statements workflow restamps it
 * (ingest-worker/src/admin.ts, the upsert batch at the upload path): during
 * that window a statement IS on file and IS being checked, so telling the
 * owner "No statement is on file… upload again" was a false statement about
 * state — and following it re-nulled the binding.
 *
 * Round 3 narrowed this further: a null binding is NOT by itself proof that a
 * check is running. Plaid-only reconciliation rows (pipeline/plaid_reconcile.py)
 * carry a null binding forever and nothing ever restamps them, so this sentence
 * is now printed only with evidence — a statement this session uploaded for
 * that account and month, or the Worker answering `not_bound` on a confirmation.
 * Without evidence the card says {@link STATEMENT_NOT_CHECKED_COPY}. It gives no
 * upload advice, because uploading again is exactly the wrong next step when a
 * check really is running.
 */
export const STATEMENT_CHECKING_COPY =
  "This statement is still being checked. The check runs once a day, at about 1pm UTC, so the review card can take until then.";

/**
 * The sentence for a row that names no file and has no evidence of a running
 * check — the Plaid-only row, or any row while the pipelines are off (rd Z8
 * round 3, MUST-FIX 1). It states the fact a null binding actually proves (no
 * check has run for this account and month yet) rather than claiming a file is
 * missing, which the page cannot know, and it gives the real next step.
 */
export const STATEMENT_NOT_CHECKED_COPY =
  "No statement has been checked for this account and month yet. Upload the statement.";

/**
 * The sentence for a row the Worker has positively told us has nothing on file
 * (`details.reason: "no_statement"` — the statements row is gone). Only then
 * is "upload the statement again" the next step.
 */
export const STATEMENT_NO_FILE_COPY =
  "No statement is on file for this account and month. Upload the statement again, then confirm.";

/**
 * The confirmation body, built from the row the Worker returned (rd Z8,
 * owner 2026-10-28: "We need the confirm button to work so the fingerprint
 * needs to be done."). `contentSha256Hex` is the fingerprint the read handed
 * back — never a value this page invents. A row that names no file draws no
 * form, so the empty-string branch is unreachable from the page; it stays as
 * the one safe shape, because the one thing this page must never do is confirm
 * a statement it cannot name.
 */
export function statementConfirmBody(
  row: Pick<StatementReconRow, "contentSha256Hex">,
  fields: { mask: string; balance: string; marker: string; cue: string },
): StatementConfirmRequest {
  const mask = fields.mask.trim();
  const marker = fields.marker.trim();
  return {
    accountMask: mask,
    markerPattern: marker.includes(mask) ? marker : `${marker} ${mask}`.trim(),
    balanceCueLabel: fields.cue.trim(),
    confirmedBalanceDecimal: fields.balance.trim(),
    contentSha256Hex: row.contentSha256Hex ?? "",
  };
}

/**
 * The success sentence, built ONLY from the figure the Worker returns on the
 * refreshed row (`statementBalanceDecimal`) — never from what was typed
 * (round 2, MUST-FIX 1). A row that comes back naming no figure gets a
 * sentence with no figure at all: the page may not print an amount the server
 * did not hand back.
 */
export function confirmedBalanceSentence(storedBalanceDecimal: string | null): string {
  const figure =
    storedBalanceDecimal === null ? "" : moneyText(storedBalanceDecimal, { fallback: "" });
  if (figure === "") return "Confirmed the ending balance.";
  return `Confirmed an ending balance of ${figure}. Next month’s statement for this account can fill in the balance on its own.`;
}

/** The four fields a Confirm sends, kept together so a rebuild moves all of them. */
interface TypedConfirmFields {
  mask: string;
  balance: string;
  marker: string;
  cue: string;
}

/** The Worker's machine-readable reason on a refused confirmation (details.reason). */
function confirmRefusalReason(body: unknown): string | null {
  if (body === null || typeof body !== "object" || !("error" in body)) return null;
  const error = (body as { error?: unknown }).error;
  if (error === null || typeof error !== "object" || !("details" in error)) return null;
  const details = (error as { details?: unknown }).details;
  if (details === null || typeof details !== "object" || !("reason" in details)) return null;
  const reason = (details as { reason?: unknown }).reason;
  return typeof reason === "string" && reason.length > 0 ? reason : null;
}

/**
 * What the card says while it is waiting for the read that decides whether a
 * failed Confirm stored anything (round 3, MUST-FIX 3). A 5xx or a lost answer
 * is not proof either way: the Worker stores the balance before the queue
 * UPDATE, and a dropped response cannot be read.
 */
export const CONFIRM_CHECKING_SAVED_COPY = "Checking whether it saved…";

/**
 * What the card says when the read that decides whether a failed Confirm
 * stored anything does not come back at all (round 4, MUST-FIX 1). The card
 * cannot say "Nothing was saved" — no read disproved it — and it may not leave
 * "Checking…" under a Confirm button that is live again, so it names the next
 * step this page really offers: the Refresh control beside it.
 */
export const CONFIRM_CHECK_FAILED_COPY =
  "Could not check whether it saved. Refresh to see.";

/**
 * What the card says when that read shows the row still `needs_review` or
 * `mismatch` — nothing was stored, so the figure can be changed and pressed
 * again (round 3, MUST-FIX 3).
 */
export const CONFIRM_NOTHING_SAVED_COPY =
  "Nothing was saved. Check the figure and try again.";

/**
 * What the card says when the read proves the save (`auto_confirmed`) but the
 * Worker named no reason. The figure is on the locked line beneath it, read
 * from the row — never from what was typed (round 3, MUST-FIX 3).
 */
export const CONFIRM_SAVED_PROVEN_COPY = "The balance was saved.";

/**
 * True for a failure whose write may still have landed. The page may claim
 * nothing until a read answers (round 3, MUST-FIX 3).
 *
 * Round 4 turned this from a list of known-ambiguous failures into one
 * exception: ONLY an `AdminApiError` below 500 is an answer on its own. A 4xx
 * comes from the Worker's own guard, which refuses before it writes, so its
 * refusal sentence is the whole truth. Everything else — a 5xx, a dropped
 * connection, and anything that is not a status at all, such as a 200 whose
 * body would not parse (the Worker may well have written first, and the press
 * still has no answer) — needs a read. Listing the ambiguous cases is how the
 * non-JSON 200 slipped through: it was neither a fetch rejection nor a 500, so
 * the card printed "That did not save. Try again." over a save that happened.
 */
export function confirmFailureNeedsRead(error: unknown): boolean {
  return !(error instanceof AdminApiError && error.status < 500);
}

/**
 * A row state a done card may stand over: the row is `auto_confirmed` and it
 * still carries the statement the press confirmed (round 3, MUST-FIX 2). A
 * newer fingerprint, or a row back in `needs_review`/`mismatch`, means the
 * finished sentence no longer describes the row.
 */
export function confirmRowMatchesDone(
  row: Pick<StatementReconRow, "outcome" | "contentSha256Hex"> | null,
  confirmedFingerprint: string | null,
): boolean {
  if (row === null || confirmedFingerprint === null) return false;
  if (row.outcome !== "auto_confirmed") return false;
  return row.contentSha256Hex === confirmedFingerprint;
}

/**
 * The row state a read must prove before the card may LOCK itself: the same
 * confirmed row, naming the figure it stored. Without the figure there is
 * nothing a read has proved, and "Saved at $X" has no X (round 3, MUST-FIX 3).
 */
export function confirmRowProvesSave(
  row: Pick<StatementReconRow, "outcome" | "statementBalanceDecimal" | "contentSha256Hex"> | null,
  confirmedFingerprint: string | null,
): boolean {
  if (!confirmRowMatchesDone(row, confirmedFingerprint)) return false;
  return row !== null && row.statementBalanceDecimal !== null;
}

/**
 * The plain sentence for each refusal the owner can actually hit, each with
 * the next step. Decided wording: "That did not save…" (rd Z1) for a write
 * that failed for no named reason. Nothing here quotes the server's text.
 */
export function statementConfirmRecovery(error: unknown, role: StaffRole): string {
  if (error instanceof AdminApiError) {
    // Decision 1 (the owner) and round 10 (the account manager reads "Ask the
    // owner.") both come from the one home.
    if (error.status === 403) return failureCopy(error, { role, context: "write" }).sentence;
    const reason = confirmRefusalReason(error.body);
    if (reason === "statement_changed") {
      return "A newer statement was uploaded for this account. Refresh to see it, then confirm again.";
    }
    if (reason === "no_statement") {
      return STATEMENT_NO_FILE_COPY;
    }
    if (reason === "not_bound") {
      // The file is on file; its check has not been stamped yet (rd Z8 round 2).
      return STATEMENT_CHECKING_COPY;
    }
    if (reason === "not_awaiting_confirmation") {
      return "This statement is not waiting for a confirmation. Refresh to see it.";
    }
    if (reason === "dispatch_failed") {
      // True where it is shown: this card keeps its fields and its Confirm
      // control, so the press the sentence names is on screen (rd Z8 round 2,
      // MUST-FIX 2). After a reload the row reads under "Waiting to be saved",
      // which promises no press — see publicationNextStep.
      return "The balance was saved, but the books did not finish updating. Press Confirm again to finish it.";
    }
    if (error.status >= 500) {
      // A bare 500 is not an answer: the Worker stores the balance before the
      // queue UPDATE, so the figure may be on file even though the response was
      // not. The page reads the row before it says anything either way
      // (round 3, MUST-FIX 3), so this is only the line shown while that read is
      // in flight — never "The balance was saved", which no read has proved.
      return CONFIRM_CHECKING_SAVED_COPY;
    }
  }
  // Everything else — a refused 4xx, a lost answer, a non-HTTP fault — reads
  // the one home. A lost answer or a 5xx may have landed, so it reads "we could
  // not confirm it saved" and never claims nothing was saved (B4b-2).
  return failureCopy(error, { role, context: "write" }).sentence;
}

/**
 * True only for a credit-card liability — the account class the closing-date
 * field is asked for, and the only one whose closing day the books take from a
 * statement. Same rule as `pipeline.reports.is_credit_card_account` (a whole
 * `CreditCard` segment) and the Worker's `statementAccountIsCreditCard`: a loan
 * sits under `Liabilities:` too and has no statement closing day, so it is
 * never asked (rd14 T2 fix round 3).
 */
export function statementAccountIsCreditCard(account: string): boolean {
  return (
    account.startsWith("Liabilities:") && account.split(":").includes("CreditCard")
  );
}

/** The label on the upload form's closing-date field. Plain, no ids. */
export const STATEMENT_CLOSING_DATE_LABEL = "Statement closing date";

/**
 * The day Books has for this card and month, or null when the page cannot
 * honestly name one.
 *
 * The row's date is the day Books checked the month against: the card's own
 * statement date from the bank when Plaid gave one, else the hand-set cycle
 * day, else month end (`pipeline/plaid_reconcile.py:resolve_closing_date`).
 * A `needs_review` row for an unknown cycle carries the 1st of the month as a
 * placeholder (the same rule the confirm card follows) — that is not a date any
 * statement closed on, so it is never printed as one.
 */
export function booksClosingDateForRow(
  row: StatementReconRow | undefined,
  period: string,
): string | null {
  if (row === undefined) return null;
  if (dateText(row.closingDate) === "") return null;
  if (row.outcome === "needs_review" && row.closingDate === `${period}-01`) {
    return null;
  }
  return row.closingDate;
}

/**
 * When the daily statement check normally runs — 13:00 UTC, from
 * `.github/workflows/statements.yml` (`cron: "0 13 * * *"`, the one schedule
 * that reads uploaded statements and restamps their rows).
 *
 * The page cannot start that run, so it says when it is due instead of telling
 * a person to refresh in a few minutes (rd16 U, C2).
 */
export const STATEMENT_CHECK_HOUR_UTC = 13;

/**
 * How long after the scheduled hour an unprocessed upload is called late
 * (rd16 U, C2). A GitHub scheduled run can start late by an hour or more and
 * then has to work through the month's statements, so four hours is the wait
 * before the page says the check looks stuck (rd16 U review, item 8: one hour
 * was too tight for the scheduler).
 */
export const STATEMENT_CHECK_LATE_AFTER_MS = 4 * 60 * 60 * 1000;

/**
 * The scheduled check that should have read an upload made at `atMs`, or null.
 *
 * The schedule is a UTC cron, so the day is taken from the value's own UTC text
 * and the run is that day at {@link STATEMENT_CHECK_HOUR_UTC} UTC. It is written
 * in ISO text on purpose: this page may not derive a PERIOD from a UTC clock
 * (owner decision 10 — the month a person reads is their own), and the source
 * guard `rd-r12-b4b` holds that line for the whole file. A UTC schedule is a
 * different question from a UTC month, and this keeps the two visibly apart.
 */
function nextCheckAt(atMs: number): number | null {
  if (!Number.isFinite(atMs)) return null;
  const day = new Date(atMs).toISOString().slice(0, 10);
  const due = Date.parse(
    `${day}T${String(STATEMENT_CHECK_HOUR_UTC).padStart(2, "0")}:00:00.000Z`,
  );
  if (!Number.isFinite(due)) return null;
  return atMs <= due ? due : due + 24 * 60 * 60 * 1000;
}

/**
 * True when the daily check that should have read this upload has had its hour
 * and still has not produced anything (rd16 U, C2). Facts only: the upload's
 * own timestamp and the schedule; the page invents no status the Worker does
 * not report.
 */
export function statementCheckLooksLate(uploadedAt: string, now: Date): boolean {
  const atMs = Date.parse(uploadedAt);
  const due = nextCheckAt(atMs);
  if (due === null) return false;
  return now.getTime() > due + STATEMENT_CHECK_LATE_AFTER_MS;
}

/**
 * What a saved statement is waiting for (rd16 U, C2). The upload is on file and
 * nothing has read it yet; the one thing the page knows about when that
 * happens is the schedule, so it says the schedule.
 */
export const STATEMENT_AWAITING_CHECK_COPY =
  "Waiting for the daily check, which runs once a day at about 1pm UTC.";

/**
 * What a statement that has waited past that window says (rd16 U, C2). It says
 * the check is late and the one thing a person can do about it — nothing on
 * this page starts the run.
 */
export const STATEMENT_CHECK_LATE_COPY =
  "The daily check has not read this statement yet, so it is late. Nothing here can start that check: ask GV to look at the daily statement check.";

/**
 * The sentence for one saved statement's check state: waiting, or late.
 * `null` for a timestamp that cannot be read (nothing is claimed).
 */
export function statementCheckStateCopy(uploadedAt: string, now: Date): string | null {
  if (dateText(uploadedAt) === "") return null;
  return statementCheckLooksLate(uploadedAt, now)
    ? STATEMENT_CHECK_LATE_COPY
    : STATEMENT_AWAITING_CHECK_COPY;
}

/**
 * True when the Worker has since answered for this upload (rd16 U review,
 * item 1).
 *
 * THE DEFECT. The waiting/late sentence was built from `uploaded_at` and the
 * schedule alone, so a person who came back to the page after the statement
 * had been checked still read "it is late… ask GV" directly beside a live
 * review card. A page must never claim a check has not happened when the row
 * in front of it proves it has.
 *
 * WHAT COUNTS AS ANSWERED. A reconciliation row for the same company, month and
 * account that names a file (`contentSha256Hex`): that is the Worker's own
 * statement binding, the same fingerprint `sessionUploadFor` matches. A bound
 * row means the check for that account and month has run — including the case
 * where a newer upload replaced this one, which also makes "late" untrue for
 * this row.
 */
export function statementUploadIsChecked(
  upload: Pick<SessionUpload, "company" | "period" | "account" | "contentSha256Hex">,
  rows: ReadonlyArray<
    Pick<StatementReconRow, "company" | "period" | "account" | "contentSha256Hex">
  >,
): boolean {
  // The row must name THIS upload's file. A replacement upload leaves the old
  // row's binding in place for a moment, and "a row is bound" alone would hide
  // the new upload's own waiting sentence (rd16 U review round 2, item 5).
  return rows.some(
    (row) =>
      row.company === upload.company &&
      row.period === upload.period &&
      row.account === upload.account &&
      typeof row.contentSha256Hex === "string" &&
      row.contentSha256Hex.length > 0 &&
      row.contentSha256Hex === upload.contentSha256Hex,
  );
}

/**
 * The two plain hints beside the closing-date field (rd14 T2 fix round 3).
 *
 * The person has the statement in front of them, so the date they need to see
 * is the one Books currently has for this card and month — the date Plaid gave
 * when Plaid gave one. The statement's own date is read after the upload and
 * compared with the day Books used; a disagreement raises the normal mismatch
 * card, which says both dates. Neither hint is an id, a mask or a path.
 *
 * rd16 U, C5: both were long enough to push the Upload button down the page.
 * Same meaning, fewer words: what Books has, and what the typed date does.
 */
export function statementClosingDateHints(opts: {
  period: string;
  booksClosingDate: string | null;
}): { booksDate: string; whatTheDateDoes: string } {
  const month = monthText(opts.period) || "this month";
  const day = opts.booksClosingDate === null ? "" : dateText(opts.booksClosingDate);
  return {
    booksDate:
      day === ""
        ? `Books has no closing date for this card in ${month} yet. It uses the bank's date when the bank gives one.`
        : `Books has this card closing on ${day} for ${month}.`,
    whatTheDateDoes: `The date you enter is used for ${month}. Books also reads the statement's own date: if the two differ, it tells you and the month waits.`,
  };
}

/**
 * The plain sentence that tells a person how to correct a card's closing day
 * (fix round 4, LOW 2), or null for an account that has no closing day.
 *
 * A card mismatch row uploaded before the upload form had a closing-date field
 * carries no entered date, so Books checked the month on the card's own
 * statement date from the bank — and Confirm would write the statement's
 * balance on that day. The one supported correction is the date the upload form
 * already asks for, so the card says it, in words, using only what the row has
 * (its account); no new field and no second card.
 */
export function cardMismatchClosingDateAdvice(account: AccountPath): string | null {
  // Classified through the key-text boundary, never by planting a raw path in
  // words (the brand guard's rule; `accountText` is the same helper the page
  // uses for the upload result keys).
  if (!statementAccountIsCreditCard(accountText(account))) return null;
  return "If this statement closed on a different day from the one Books used, upload it again with its closing date: the date you enter is the day Books records this ending balance on.";
}

/**
 * What to say about the date typed into the field, or null when it can be
 * used. Empty is only acceptable for an account that is not a card.
 */
export function statementClosingDateProblem(
  account: string,
  rawValue: string,
  period: string,
): string | null {
  const value = rawValue.trim();
  if (value === "") {
    return statementAccountIsCreditCard(account)
      ? "Enter the closing date printed on the statement. A card statement needs it."
      : null;
  }
  if (!isValidPeriod(period)) {
    return "Pick a valid month first.";
  }
  if (dateText(value) === "") {
    return "That closing date is not a date Books can read. Check the day and try again.";
  }
  const year = Number(period.slice(0, 4));
  const month = Number(period.slice(5, 7));
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const first = `${period}-01`;
  const last = `${period}-${String(lastDay).padStart(2, "0")}`;
  if (value < first || value > last) {
    return `The closing date has to be a day in ${monthText(period) || "the month you picked"}. If this statement closes in another month, upload it against that month.`;
  }
  return null;
}

/** The short file check, shown beside the account behind "Show details". */
function shortFileCheck(contentSha256Hex: string): string {
  return contentSha256Hex.length > 12
    ? `${contentSha256Hex.slice(0, 12)}…`
    : contentSha256Hex;
}

/** One account's kind, as the ledger view reports it (`ledgerViewAccountSchema`). */
export interface StatementAccountKind {
  type: string;
  isCash: boolean;
}

/**
 * True when an account is certainly a bank or card account: the FIRST band of
 * the picker (rd16 U, C3; orchestrator decision 2026-11-03).
 *
 * rd13 L1 — WHAT THE KIND ADDS. `isCash` is the books' own word for "money in a
 * bank or wallet account" (`pipeline/reports.py` `is_cash_account`, reported
 * per account by `pipeline/ledger_view.py`), and a card is a liability the
 * books name with their own `CreditCard` segment
 * (`pipeline/onboarding_accounts.py`), so the ledger read can recognise an
 * account whose PATH this page would not.
 *
 * This is NOT the picker's whole list any more. An unrecognised account is
 * offered under "Other accounts" (`statementAccountGroups`), because hiding a
 * real bank account is worse than listing an account that is not one — the
 * orchestrator's decision after round 16 first shipped the narrow rule.
 */
export function statementAccountCanHaveStatement(
  account: string,
  kind: StatementAccountKind,
): boolean {
  // `isCash` is the books' own word for money in a bank or wallet account
  // (`pipeline/reports.py` `is_cash_account`), and it is authoritative when the
  // ledger reported this account.
  if (kind.isCash) return true;
  // The kind alone cannot answer it either: a bank account onboarding opened
  // for a Plaid depository is `Assets:<co>:Bank:<leaf>-<mask>`
  // (`pipeline/onboarding_accounts.py` `proposed_ledger_account`), and the
  // books' cash word list does not contain `Bank`, so `isCash` is false for it.
  // The path's own segment is the answer, and it is the same word the books
  // opened the account with.
  return isBankOrCardPath(account);
}

/** `Bank` — the segment onboarding gives a depository account. */
const BANK_PATH_SEGMENT = "Bank";

/**
 * The books' own cash segments (LOCKSTEP with `pipeline/reports.py`
 * `is_cash_account`, the function behind the ledger view's `isCash`). Needed
 * only where the ledger's per-account answer is not available.
 */
const CASH_PATH_SEGMENTS: ReadonlySet<string> = new Set([
  "Cash",
  "Checking",
  "Savings",
  "Operating",
]);

/**
 * True for the paths a bank statement certainly arrives for, by the books' own
 * segments: a cash account (`Assets:…:Operating`, and the wallet's
 * `Assets:…:Moov:Operating`), a depository onboarding opened
 * (`Assets:…:Bank:…`) or a card (`…:CreditCard:…`).
 *
 * rd16 U, C3: this is the FIRST group's test, not the picker's whole list. An
 * account it does not recognise is offered under "Other accounts" — a real bank
 * account must never become impossible to upload a statement for (orchestrator
 * decision, 2026-11-03).
 */
export function isBankOrCardPath(account: string): boolean {
  if (statementAccountIsCreditCard(account)) return true;
  const parts = account.split(":");
  if (parts[0] !== "Assets") return false;
  if (parts.includes(BANK_PATH_SEGMENT)) return true;
  return parts.some((part) => CASH_PATH_SEGMENTS.has(part));
}

/**
 * The two headings the picker paints (rd16 U, C3; orchestrator decision
 * 2026-11-03). Plain English, no ids, no count: the count line above the list
 * already says how many are offered.
 */
export const STATEMENT_ACCOUNT_RECOGNISED_LABEL = "Bank and card accounts";
export const STATEMENT_ACCOUNT_OTHER_LABEL = "Other accounts";

/** The picker's two bands, in the order they are painted. */
export interface StatementAccountGroups {
  /** Bank and card accounts: the shapes a statement certainly arrives for. */
  recognised: string[];
  /**
   * The remaining ASSETS that could still be one the page cannot name (an
   * Escrow, a hand-made `Assets:…:Chase:Main`). Offered so nothing is
   * impossible to pick; never claimed to be a bank. A liability appears here
   * only if it names a card or credit, and then it is in `recognised`.
   */
  other: string[];
}

/**
 * Segments that make an account certainly NOT a bank or card account. Reused
 * rules first, then the books' own chart words (rd16 U, C3; orchestrator
 * decision round 2, 2026-11-03, from the TEST Rental chart):
 *
 *   - money between our own companies — `isBetweenCompaniesAccount`
 *     (`ui/display-words.ts`, `pipeline/intercompany.py`);
 *   - a loan — `pipeline/reports.py` `is_loan_liability` (a segment that IS
 *     `Loan` or starts with it), which `ui/audit-accounts.ts` also refuses;
 *     this catches `Loans:…` and an asset such as `LoanOriginationFees`. A
 *     liability that names no card is dropped by `statementAccountGroups`'
 *     liability gate whatever it is called, so `PrivateLoan` and
 *     `ShortTermLoan` do not need their own words here;
 *   - a receivable — `Fee-Receivable` (`pipeline/config.py`) and the
 *     `Receivable`/`Receivables` words, which `ui/audit-accounts.ts` refuses;
 *   - a payable — `is_ap_account`'s words (`ap`, `accountspayable`, `payable`).
 *
 * Then the asset kinds the chart names that are never bank money: undeposited
 * funds, prepaid/gift-card value, and the fixed/capital asset families
 * (`CapitalImprovements`, `Development`, `Property`, `DownPayment`, land,
 * buildings, equipment, depreciation). And the liability kinds that are never a
 * bank: deposits held, tenant credits and tax.
 *
 * The direction of error is deliberate: a word this list does not know lands in
 * "Other accounts" and is still pickable. A word it wrongly excludes would hide
 * a real account, so only words that cannot name a bank account are here.
 */
/**
 * The excluded segment words, keyed WITHOUT hyphens and in lowercase, so
 * `Capital-Improvements` and `CapitalImprovements` are one key (rd16 U review
 * round 2, item 4).
 */
const ASSET_NOT_BANK_SEGMENTS: ReadonlySet<string> = new Set([
  // receivables (`Fee-Receivable`, `AccountsReceivable`, …)
  "receivable",
  "receivables",
  "accountsreceivable",
  "feereceivable",
  // payables (an asset named Payable is still a payable)
  "ap",
  "payable",
  "payables",
  "accountspayable",
  // money not yet at the bank, and prepaid/gift-card value
  "undepositedfunds",
  "prepaid",
  "prepaids",
  "prepayment",
  "prepayments",
  "giftcardsandrebates",
  // fixed / capital assets
  "capitalimprovements",
  "development",
  "property",
  "downpayment",
  "land",
  "building",
  "buildings",
  "improvements",
  "fixtures",
  "furniture",
  "equipment",
  "vehicles",
  "depreciation",
  "accumulateddepreciation",
]);

const LIABILITY_NOT_BANK_SEGMENTS: ReadonlySet<string> = new Set([
  // payables (`is_ap_account`)
  "ap",
  "payable",
  "payables",
  "accountspayable",
  // deposits the company holds for someone else, money paid ahead, tenant
  // credits and tax: never a bank account of this company's
  "securitydeposit",
  "securitydeposits",
  "petdeposit",
  "petdeposits",
  "prepayments",
  "prepayment",
  "tenantcredits",
  "tax",
  "taxes",
  "mortgage",
  "mortgages",
]);

/**
 * True when a LIABILITY names a card or a credit account: `CreditCard`,
 * `CreditCards:Operating`, `CardPayable` (rd16 U review round 2, item 4b). The
 * test is on a segment, case-insensitive, because the books name these by hand
 * as often as by onboarding.
 */
export function isCardOrCreditLiability(account: string): boolean {
  if (!account.startsWith("Liabilities:")) return false;
  return account.split(":").some((part) => /card|credit/i.test(part));
}

/**
 * True when a segment is one the chart names as not-bank money. A segment, not
 * a substring: `Assets:X:PropertyTaxEscrow` is not `Property`, and an Escrow is
 * kept on purpose (it can be a bank account at an escrow agent).
 */
function hasNotBankSegment(account: string): boolean {
  const excluded = account.startsWith("Assets:")
    ? ASSET_NOT_BANK_SEGMENTS
    : LIABILITY_NOT_BANK_SEGMENTS;
  return account.split(":").some((part) => excluded.has(segmentKey(part)));
}

/** A segment as the exclusion sets key it: lowercase, hyphens removed. */
function segmentKey(part: string): string {
  return part.toLowerCase().replace(/-/g, "");
}

/**
 * LOCKSTEP with `pipeline/reports.py` `is_loan_liability`: a segment that is
 * exactly `Loan` or starts with `Loan` (`Loans`, `LoanPayable`, …). Applied in
 * any root, the same way `ui/audit-accounts.ts` refuses `Loans` wherever it
 * appears.
 */
export function statementAccountIsLoan(account: string): boolean {
  return account
    .split(":")
    .some((part) => part === "Loan" || part.startsWith("Loan"));
}

/**
 * True when an account is certainly not a bank or card account, so it is
 * offered in NEITHER band (rd16 U, C3; orchestrator decision round 2).
 *
 * This is the ONLY exclusion. Everything else the chart holds is offered, in
 * the recognised band or under "Other accounts".
 */
export function isCertainlyNotBankAccount(account: string): boolean {
  if (!account.startsWith("Assets:") && !account.startsWith("Liabilities:")) {
    // Income, Expenses, Equity and anything else: a bank statement is a bank's
    // document, so these were never on this picker.
    return true;
  }
  if (isBetweenCompaniesAccount(account)) return true;
  if (statementAccountIsLoan(account)) return true;
  return hasNotBankSegment(account);
}

/**
 * The picker's two bands, in chart order (rd16 U, C3; orchestrator decisions
 * 2026-11-03, round 2).
 *
 * WHY TWO GROUPS AND NOT ONE LIST. Round 16 first offered ONLY the shapes it
 * recognised, and the orchestrator rejected that: a real bank account whose
 * name it did not recognise (a hand-made `Assets:X:Chase:Main`) then had no way
 * to upload a statement at all. So the list keeps every account that could be a
 * bank, in two named bands.
 *
 * WHAT LANDS WHERE.
 *   - **Bank and card accounts:** the shapes a statement certainly arrives for
 *     (a cash word, `Assets:…:Bank:…`, a card), the ledger's own `isCash`, a
 *     liability segment naming a card or credit, and any account a
 *     reconciliation row already names. A row wins over every exclusion: an
 *     account Books is already checking a statement against is a statement
 *     account, whatever its name looks like (round 2, item 6).
 *   - **Other accounts:** the remaining ASSETS only — including an Escrow or a
 *     hand-named `Chase:Main`, because either can be a bank account.
 *   - **Neither:** the exclusions above, which on the TEST Rental chart are
 *     receivables, undeposited funds, gift cards, capital/development/property
 *     assets, loans/mortgages, payables, deposits held, tenant credits and tax.
 *
 * `alsoRecognised` is the accounts the page already knows are statement
 * accounts from a read it has made (the month's reconciliation rows). No extra
 * request.
 *
 * The population is the chart's own accounts, not only the ones the month's
 * ledger read mentioned: an account with no movement in the month is exactly
 * the case where hiding it would make a statement impossible.
 */
export function statementAccountGroups(
  names: readonly string[],
  kinds?: ReadonlyMap<string, StatementAccountKind>,
  alsoRecognised?: readonly string[],
): StatementAccountGroups {
  const seen = new Set<string>();
  const known = new Set(alsoRecognised ?? []);
  const recognised: string[] = [];
  const other: string[] = [];
  for (const name of names) {
    if (seen.has(name)) continue;
    seen.add(name);
    // A reconciliation row is the strongest fact the page holds: recognition
    // runs BEFORE the exclusions so a named account can never be hidden.
    if (known.has(name)) {
      recognised.push(name);
      continue;
    }
    if (isCertainlyNotBankAccount(name)) continue;
    const kind = kinds?.get(name);
    const isRecognised =
      (kind !== undefined && kind.isCash) ||
      isBankOrCardPath(name) ||
      isCardOrCreditLiability(name);
    // A liability is only ever offered as a card/credit account; anything else
    // owed (a loan, a payable, a deposit held, a tax) is not a statement
    // account, and the exclusion list already removed those.
    if (name.startsWith("Liabilities:") && !isCardOrCreditLiability(name)) {
      continue;
    }
    if (isRecognised) recognised.push(name);
    else other.push(name);
  }
  return { recognised, other };
}

/**
 * The picker's list, flattened: every account the two bands hold.
 *
 * The upload gate calls this too, so an account offered under "Other accounts"
 * is a valid submission — a picker that shows a name the form then refuses
 * would be the same defect in another place.
 */
export function statementEligibleAccounts(
  names: readonly string[],
  kinds?: ReadonlyMap<string, StatementAccountKind>,
  alsoRecognised?: readonly string[],
): string[] {
  const groups = statementAccountGroups(names, kinds, alsoRecognised);
  return [...groups.recognised, ...groups.other];
}

/**
 * What the picker says when the month's ledger read answered with no account
 * that can take a statement. It names the month, not a path, and it claims
 * nothing about why the month is empty (`noStatementAccountText`).
 */
export function noStatementAccountText(period: string): string {
  return `No account for ${formatPeriodLabel(period)} can take a statement yet.`;
}

/** What uploading does, in one sentence. */
const PROCESSING_GATE_LEAD =
  "Uploading stores the PDF for this company. It does not change the books or close the month.";

/**
 * What uploading does, and nothing about whether statement checking is
 * switched on. Nothing this page reads proves the switch is off: Connection
 * status can show it On or leave it unknown, never Off, and a mismatch card
 * beside "checking is off" contradicted the page itself (round eight B1). A
 * sentence about the switch belongs here only if a read proves it.
 */
export function processingGateCopy(): string {
  return PROCESSING_GATE_LEAD;
}

/**
 * After the wait runs out with no review card. It says what is known (the PDF
 * is saved, nothing came back yet) and what it is waiting for: the daily check,
 * on the schedule the page can name (rd16 U, C2). Before, it said "Refresh in a
 * minute", which now reads as a promise the nightly run cannot keep.
 */
export function processingTimeoutCopy(role: StaffRole): string {
  // The sentence is the same for both roles; the parameter stays so the page
  // keeps one call shape and a later role split has a place to live.
  void role;
  // The card appears once the check finishes; neither role can make it appear,
  // so the account manager is not told to ask the owner for a step the owner
  // cannot take either (B4b-3).
  return `No review card yet. The PDF is saved. ${STATEMENT_AWAITING_CHECK_COPY}`;
}

/** Kept for callers that imported the old constants. */
export const PROCESSING_GATE_COPY = processingGateCopy();
export const PROCESSING_TIMEOUT_COPY = processingTimeoutCopy("account_manager");

/**
 * The readable account name. One helper for the whole console (rd Y0); it
 * drops the company code in the middle of a path itself (rd Y2), so there is
 * no second rule here.
 */
function accountFace(name: string, companies: AccountLabelContext): string {
  return accountLabel(name, companies);
}

/**
 * What the page is doing, in the account's own words. The ledger path stays
 * behind Show details (rd Z4 C-11): a person reading "Processing
 * Assets:GVPM:Chase:Operating" is handed an internal path, not a name.
 */
export function processingSentence(accountName: string): string {
  return `Processing ${accountName}… usually ready in 30–60 seconds.`;
}

/**
 * The mismatch sentence (rd Z4 C-11; the shared sentence in
 * docs/design/rd-z-fixes-background.md §3). Three figures, and each one says
 * whose it is: the bank's figure is never offered as the books' figure.
 *
 * A books figure is printed only when it is FOR THE SAME DATE as the other two
 * (round-2 MUST-FIX 1): the ledger's `closing` is the balance at period end,
 * while the bank and statement figures are as of the account's statement cycle
 * day (pipeline/ledger_view.py `_accounts_for_period`, pipeline/plaid_reconcile.py
 * `period_closing_date`). Otherwise the sentence names the two figures it has
 * and makes no claim about the books; when the read itself failed it says that
 * much and nothing more — never "the books balance is on Bank accounts", which
 * Bank accounts does not show (round-2 MUST-FIX 3).
 */
export function mismatchFiguresCopy(figures: {
  bank: string | null;
  statement: string | null;
  books: string | null;
  booksReadFailed: boolean;
}): string {
  const bank = moneyText(figures.bank, { fallback: "no balance" });
  const statement = moneyText(figures.statement, { fallback: "no balance" });
  const measured = `The bank shows ${bank}. The statement shows ${statement}.`;
  if (figures.books === null) {
    return figures.booksReadFailed
      ? `${measured} The books balance could not be loaded.`
      : measured;
  }
  const books = moneyText(figures.books, { fallback: "no balance" });
  return `The books show ${books}. ${measured}`;
}

/**
 * The one sentence that refuses an empty Confirm on a mismatch (rd13 L4, owner
 * framing: "it is just a double check basically"). The statement's own figure
 * is on the row, so a filled-in box let one press confirm the figure that was
 * already known to disagree — the double check answered itself.
 */
/**
 * The owner's next step on a mismatch (rd13, owner decision 2026-11-01,
 * option 1). The check compares the statement with the bank's own reading on
 * the closing date, not with the books, so fixing a transaction in the books
 * does not clear it and the old "find the missing or extra transaction" sent
 * the owner the wrong way. The sentence names the usual causes, what typing the
 * figure does, and that the month stays open until then (close refuses an
 * unreconciled account, `pipeline/close.py`).
 */
export const MISMATCH_NEXT_STEP_COPY =
  "The statement and the bank disagree. This usually means the statement was uploaded to the wrong account, the account's closing day is set wrong, the balance was misread from the PDF, or the bank took its reading at a different time. Check the statement, then type its ending balance and confirm: the books must then match that figure. The month stays open until you do.";

export const MISMATCH_EMPTY_CONFIRM_COPY =
  "Type the ending balance from the statement before you confirm.";

/** Absolute cents as a contract decimal (`10.00`), so `moneyText` prints it. */
function absCentsDecimal(cents: bigint): string {
  const abs = cents < 0n ? -cents : cents;
  return `${(abs / 100n).toString()}.${(abs % 100n).toString().padStart(2, "0")}`;
}

/** What the difference sentence is built from: the row's two figures and its date. */
export interface MismatchDifference {
  bank: string | null;
  statement: string | null;
  closingDate: string;
  period: string;
  role: StaffRole;
}

/**
 * Say how far apart the statement and the bank are, in plain words, on the
 * date the two figures were taken (rd13 L4 item 1).
 *
 * The comparator is the BANK's figure because that is the pair the check
 * compares (pipeline/pdf_reconcile.py: the statement's ending balance against
 * the Plaid balance snapshot for the closing date), and both are dated the same
 * day. The books figure printed beside it is a third number, dated at month end
 * and possibly behind by bank lines not booked yet, so subtracting it would
 * report a gap the check never measured.
 *
 * A row that cannot supply both figures says what it can and that no comparison
 * was made — never a direction it did not measure (rd Z4's rule, kept).
 */
export function mismatchDifferenceCopy(input: MismatchDifference): string {
  // The closing date is the account's own statement cycle day, straight from
  // the row (pipeline/plaid_reconcile.py `period_closing_date`): an off-cycle
  // credit card compares on its cycle day, never on the month's last day.
  const date =
    dateText(input.closingDate) || monthText(input.period) || "the statement date";
  const statementCents =
    input.statement === null ? null : decimalToCents(input.statement);
  if (statementCents === null) {
    return sentence("statement_mismatch_no_statement", input.role, { date });
  }
  const bankCents = input.bank === null ? null : decimalToCents(input.bank);
  if (bankCents === null) {
    return sentence("statement_mismatch_no_bank", input.role, {
      statement: moneyText(input.statement, { fallback: "no balance" }),
      date,
    });
  }
  const gap = statementCents - bankCents;
  if (gap === 0n) {
    // Equal figures with a mismatch outcome cannot come from the pipeline; if a
    // stale row says it, the page states the two equal figures and claims no
    // direction it did not measure.
    return sentence("statement_mismatch_same", input.role, {
      amount: moneyText(input.statement, { fallback: "no balance" }),
      date,
    });
  }
  return sentence("statement_mismatch_difference", input.role, {
    difference: moneyText(absCentsDecimal(gap), { fallback: "no difference" }),
    side: gap > 0n ? "higher" : "lower",
    date,
  });
}

/** The books figure for a mismatch, and whether the read that would give it failed. */
export interface BooksFigure {
  books: string | null;
  booksReadFailed: boolean;
}

/**
 * True only when the statement's own date is the date the books figure is for:
 * the last day of the period. A mid-month cycle day is a different date, and
 * two dates must never share one sentence.
 */
export function booksShareStatementDate(closingDate: string, period: string): boolean {
  const match = /^(\d{4})-(\d{2})$/.exec(period);
  if (match === null) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return false;
  }
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return closingDate === `${period}-${String(lastDay).padStart(2, "0")}`;
}

/**
 * True when the page should name the statement's closing date beside the
 * month: the row carries a date and it is not the month's last day, so the
 * account closed on a day of its own and "October 2026" alone would let a
 * person assume a month-end comparison.
 *
 * For a credit card the row's date is the day Books checked the month against
 * (`pipeline/plaid_reconcile.py:resolve_closing_date`): the card's own statement
 * date from the bank when Plaid has one, else the hand-set cycle day, else
 * month end. That is why the "Saved to the books" row prints it too (rd14 T2,
 * fix round 2): the owner asked to see which day the books used for a month
 * Plaid already settled, without a new field or a review card.
 */
export function showsClosingDate(closingDate: string, period: string): boolean {
  if (dateText(closingDate) === "") return false;
  return !booksShareStatementDate(closingDate, period);
}

/**
 * The books figure to print beside the bank and the statement, decided in one
 * place (round-2 MUST-FIX 1, 2, 3):
 *
 *   * the read failed -> no figure, and the sentence says it could not load;
 *   * the dates differ -> no figure and no claim about the books;
 *   * the account is absent from a successful read -> the Worker omits an
 *     account it never touched, so the books figure is $0.00;
 *   * a liability is shown debt-positive, the convention the figures beside it
 *     use (pipeline/plaid_reconcile.py `beancount_balance_amount` negates
 *     Plaid's debt-positive `current` for the beancount assertion, so the
 *     ledger keeps a card owing $500 at -500.00).
 */
export function booksFigureFor(input: {
  account: AccountPath;
  closingDate: string;
  period: string;
  balances: ReadonlyMap<string, string> | null;
}): BooksFigure {
  if (input.balances === null) return { books: null, booksReadFailed: true };
  if (!booksShareStatementDate(input.closingDate, input.period)) {
    return { books: null, booksReadFailed: false };
  }
  const closing = input.balances.get(input.account) ?? "0.00";
  if (!accountText(input.account).startsWith("Liabilities:")) {
    return { books: closing, booksReadFailed: false };
  }
  return { books: debtPositiveAmount(closing), booksReadFailed: false };
}

/**
 * The same amount on the figures' sign convention, in exact cents. Parsed
 * first, so a value that is not an amount never reaches the sentence and never
 * becomes a number JavaScript added up.
 */
function debtPositiveAmount(amount: string): string | null {
  const cents = decimalToCents(amount);
  if (cents === null) return null;
  const flipped = -cents;
  const negative = flipped < 0n;
  const abs = negative ? -flipped : flipped;
  const whole = (abs / 100n).toString();
  const frac = (abs % 100n).toString().padStart(2, "0");
  return `${negative ? "-" : ""}${whole}.${frac}`;
}

export interface StatementsPageOptions {
  client: AdminClient;
  company: CompanySlug;
  root: HTMLElement;
  isCurrent?: () => boolean;
  companyLabels?: Map<string, string>;
  /**
   * The shell's ledger segment -> company name read (`face.companySegmentNames`,
   * rd13 L1, review round 2). Absent in a unit test or a session that could not
   * read the segments; the label context then falls back to the slug.
   */
  companySegments?: ReadonlyMap<string, string>;
  /**
   * The reader's role, as the shell holds it (owner decision 10: an unknown
   * role reads as the account manager, never the owner).
   */
  role?: StaffRole;
}

export function renderStatementsPage(opts: StatementsPageOptions): void {
  const { client, company, root } = opts;
  const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;
  const role = readerRole(opts.role);
  clear(root);

  // Owner decision 10 and item B4b-4: the local month, never later than the
  // UTC month the Worker has reached.
  let period = periodNotAfterUtc();
  const labels = opts.companyLabels ?? new Map<string, string>();
  // The page's ONE account-label context, built once from the shell's names
  // (rd13 L1): a statement's account names the company it is with.
  const companies = face.accountLabelContext(opts.companyLabels, opts.companySegments);
  // The shell supplies display names (rd Y1); the one labeller still decides,
  // so a label that is only a key never reaches the face.
  const dirRows: CompanyDirectoryRow[] = [...labels].map(([slug, displayName]) => ({
    slug,
    displayName,
  }));
  const dir = createCompanyDirectory(dirRows, {
    complete: true,
    segments: opts.companySegments ?? new Map<string, string>(),
  });
  const labelFor = (slug: CompanySlug): string => face.company(slug, dir, "sentence");

  const header = pageHeader(
    navLabel("statements"),
    // The account manager has no Confirm control on this page, so the lede
    // must not tell them to confirm (rd round 11, R7-11).
    role === "owner"
      ? "Upload a statement PDF for the selected company, month, and account. After processing, confirm any review cards below."
      : "Upload a statement PDF for the selected company, month, and account. The owner confirms the ending balance on each card below.",
  );
  header.querySelector("h1")?.setAttribute("data-role", "statements-title");
  header.querySelector("p")?.setAttribute("data-role", "statements-lede");
  root.append(header);

  const periodHost = h("div", { class: "toolbar", "data-role": "statements-toolbar" });
  periodHost.append(
    createPeriodControl({
      period,
      ariaLabel: "Statement period",
      onChange: (next) => {
        period = next;
        // The account list is the CHOSEN month's ledger (rd13 L1, review round
        // 2): an account quiet in the month the page opened on and active in
        // this one must be offered. One read per month, cached below.
        reloadAccountKinds();
        // The closing-date field is bounded to the month it is for, and the
        // day Books has is per month.
        paintClosingDateField();
        void refreshRecons();
      },
    }),
  );
  root.append(periodHost);

  // The page's sentences say "Refresh" (rd round 11, R7-10a), so the control
  // they name is on the page: the shared caption, wired to the same read the
  // poll uses. Pressing it drops the read cache first, because a person who
  // asks for a refresh is asking for the server's newest answer.
  let lastLoadedAt = new Date();
  const freshnessHost = h("div", { "data-role": "statements-freshness" });
  // One caption node, kept across reads: a poll every five seconds must not
  // replace the button under the person's focus.
  let freshness: HTMLElement | null = null;
  const paintFreshness = (): void => {
    if (freshness === null) {
      freshness = createFreshnessCaption({
        updatedAt: lastLoadedAt,
        onRefresh: () => {
          // A person asking for a refresh is asking for the server's newest
          // answer, including the books figure the caption's age describes
          // (round 2, MUST-FIX 5).
          forgetBooksRead();
          settleReads(client);
          void refreshRecons();
        },
      });
      freshnessHost.append(freshness);
      return;
    }
    // The caption is `Updated … · Refresh`: replace only its own leading text
    // node, never the separator the shared caption already wrote (round 2,
    // SHOULD-FIX 7). Do not assume the first child is a text node — find one.
    const time = firstTextNode(freshness);
    if (time !== null) time.textContent = formatFreshness(lastLoadedAt);
  };
  paintFreshness();
  root.append(freshnessHost);

  root.append(
    h("p", { class: "muted", "data-role": "statement-processing-gate" }, [
      processingGateCopy(),
    ]),
  );

  const accountHost = h("div", { "data-role": "statement-account-host" });
  const fileInput = h("input", {
    type: "file",
    class: "field-input",
    accept: "application/pdf,.pdf",
    "aria-label": "Statement PDF",
    "data-role": "statement-file",
  });
  fileInput.accept = "application/pdf,.pdf";

  const uploadBtn = button("Upload PDF", "primary", {
    "data-action": "upload-statement",
    "aria-label": "Upload PDF",
  });

  // The closing date printed on a card statement (rd14 T2 fix round 3, owner
  // grant G14-3): the person uploading has the PDF in front of them, and the
  // bank's own statement dates move. STARTS EMPTY and is never pre-filled — a
  // filled-in date would be this page answering the question it asks. Shown
  // only for a credit card; a bank statement has no closing day to choose.
  const closingDateInput = h("input", {
    type: "date",
    class: "field-input",
    "aria-label": STATEMENT_CLOSING_DATE_LABEL,
    "data-role": "statement-closing-date",
  });
  const closingDateBooksLine = h("p", {
    class: "muted",
    "data-role": "statement-closing-date-books",
  });
  const closingDateWhatLine = h("p", {
    class: "muted",
    "data-role": "statement-closing-date-what",
  });
  // Hidden until a credit card is chosen; `hidden` is what the CSS rule
  // `.statements-upload .file-label[hidden]` turns into `display: none` (the
  // class sets `display: flex`, which would otherwise beat the attribute).
  const closingDateField = h(
    "label",
    {
      class: "file-label field",
      "data-role": "statement-closing-date-field",
      hidden: "true",
    },
    [
      h("span", { class: "field-label" }, [STATEMENT_CLOSING_DATE_LABEL]),
      closingDateInput,
      closingDateBooksLine,
      closingDateWhatLine,
    ],
  );

  const status = h("p", {
    class: "status",
    role: "status",
    "data-role": "upload-status",
  });

  /**
   * The closing date, checked when the field is LEFT as well as on Upload
   * (rd16 U, C1). Before, an out-of-month date was only refused after the
   * person pressed Upload, so the mistake was announced at the end of the form
   * instead of at the field that made it.
   *
   * The check is the same function Upload uses, so the field and the button
   * cannot disagree, and it runs only when the field has a value: leaving an
   * empty field is not an error yet, and the required-date sentence belongs to
   * the press.
   */
  let lastClosingDateProblem: string | null = null;
  const checkClosingDateOnLeave = (): void => {
    const account = accountChooser?.getValue() ?? "";
    if (!statementAccountIsCreditCard(account)) return;
    const value = closingDateInput.value.trim();
    if (value === "") return;
    const problem = statementClosingDateProblem(account, value, period);
    if (problem === null) {
      if (status.className === "status banner-err" && status.textContent === lastClosingDateProblem) {
        clear(status);
        status.className = "status";
        lastClosingDateProblem = null;
      }
      return;
    }
    lastClosingDateProblem = problem;
    status.className = "status banner-err";
    clear(status);
    status.append(document.createTextNode(problem));
  };
  closingDateInput.addEventListener("blur", checkClosingDateOnLeave);
  closingDateInput.addEventListener("change", checkClosingDateOnLeave);

  const form = h("div", { class: "statements-upload card", "data-role": "statements-upload" }, [
    h("label", { class: "file-label field" }, [
      h("span", { class: "field-label" }, ["Account"]),
      accountHost,
    ]),
    h("label", { class: "file-label field" }, [
      h("span", { class: "field-label" }, ["PDF file"]),
      fileInput,
    ]),
    closingDateField,
    uploadBtn,
    status,
  ]);
  root.append(form);

  let accountChooser: ReturnType<typeof mountChartChooser> | null = null;
  let chartLoad: ChartLoadState | null = null;
  /** Which accounts can have a statement, from the ledger's own kinds. */
  let accountKinds: ReadonlyMap<string, StatementAccountKind> | undefined;
  /** The rows the page last painted: the day Books has for a card and month. */
  let paintedRows: readonly StatementReconRow[] = [];
  /**
   * True once a reconciliation read for the month on screen has PAINTED
   * (rd16 U review round 2, item 3). A waiting/late sentence is a claim about
   * what the Worker has not done, so it may only be said when the page holds the
   * answer for that upload's month: before the first read settles, nothing is
   * claimed.
   */
  let rowsPainted = false;
  /**
   * The accounts the chooser was last painted with as "already a statement
   * account" (the month's reconciliation rows). A new one repaints the bands
   * once (rd16 U review round 2, item 6).
   */
  let paintedRecognition: readonly string[] = [];

  /**
   * Which account the date in the field was typed for (fix round 4, LOW 3).
   *
   * The date belongs to the statement in the person's hand. A repaint for the
   * same account keeps it, but choosing a DIFFERENT account clears it: a date
   * carried to another card is a date nobody read off that card's statement,
   * and the anchoring rule is that no field arrives already filled.
   */
  let closingDateAccount: string | null = null;

  /**
   * Show the closing-date field only for a credit card, and say beside it which
   * day Books has for this card and month (the date Plaid gave, when Plaid gave
   * one) plus what the entered date does (rd14 T2 fix round 3).
   */
  const paintClosingDateField = (): void => {
    const account = accountChooser?.getValue() ?? "";
    const isCard = account.length > 0 && statementAccountIsCreditCard(account);
    if (
      closingDateAccount !== null &&
      account.length > 0 &&
      account !== closingDateAccount
    ) {
      closingDateInput.value = "";
      lastClosingDateProblem = null;
    }
    if (account.length > 0) closingDateAccount = account;
    closingDateField.hidden = !isCard;
    closingDateInput.required = isCard;
    if (!isCard) closingDateInput.value = "";
    if (isValidPeriod(period)) {
      const year = Number(period.slice(0, 4));
      const month = Number(period.slice(5, 7));
      const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
      closingDateInput.min = `${period}-01`;
      closingDateInput.max = `${period}-${String(lastDay).padStart(2, "0")}`;
    }
    const row = paintedRows.find(
      (candidate) => candidate.account === account && candidate.period === period,
    );
    const hints = statementClosingDateHints({
      period,
      booksClosingDate: isCard ? booksClosingDateForRow(row, period) : null,
    });
    closingDateBooksLine.textContent = hints.booksDate;
    closingDateWhatLine.textContent = hints.whatTheDateDoes;
  };
  /** The accounts the company offers, so a statement's account is told from a twin. */
  const offeredAccounts = (): readonly string[] => chartLoad?.snapshot.selectableAccounts ?? [];
  /**
   * The accounts this page already knows are statement accounts, from the read
   * it has in hand: the month's reconciliation rows exist only for an account
   * Books reconciles a statement against (rd16 U, C3). No extra request — this
   * is the one list the page already holds.
   */
  const statementAccountNames = (): readonly string[] =>
    paintedRows
      .filter((row) => row.period === period)
      .map((row) => row.account);
  // The confirm and status cards are worded with the chart in hand, so they wait
  // for the chart read to settle (it succeeded or failed) before they paint.
  let chartSettled: () => void = () => undefined;
  const chartRead = new Promise<void>((resolve) => {
    chartSettled = resolve;
  });

  const paintAccountChooser = (load: ChartLoadState): void => {
    chartLoad = load;
    clear(accountHost);
    // rd16 U, C3 (orchestrator decision 2026-11-03): two bands — the bank and
    // card shapes first, then every other balance-sheet account, so a real bank
    // account this page cannot name is still pickable. The month's
    // reconciliation rows are recognised for free: an account Books already
    // reconciles a statement against is a statement account.
    const knownNames = statementAccountNames();
    paintedRecognition = [...knownNames];
    const groups = statementAccountGroups(
      load.snapshot.selectableAccounts,
      accountKinds,
      knownNames,
    );
    const eligible = [...groups.recognised, ...groups.other];
    // A re-paint (a new month, a refresh) keeps the account already chosen
    // when this month offers it, so a month change does not clear a field the
    // person filled.
    const chosenBefore = accountChooser?.getValue() ?? null;
    accountChooser = mountChartChooser({
      kind: "account",
      selectable: eligible,
      className: "input",
      ariaLabel: "Statement account",
      placeholder: "Choose a bank or credit card account",
      load,
      companies,
      groups: {
        primaryLabel: STATEMENT_ACCOUNT_RECOGNISED_LABEL,
        primary: groups.recognised,
        restLabel: STATEMENT_ACCOUNT_OTHER_LABEL,
      },
      ...(chosenBefore !== null && eligible.includes(chosenBefore)
        ? { initialValue: chosenBefore }
        : {}),
    });
    accountChooser.filter.setAttribute("data-role", "statement-account");
    accountHost.append(accountChooser.root);
    // The closing-date field appears for a credit card and disappears for a
    // bank account. The chooser announces a choice on its own filter's `change`
    // event, and typing a full name fires it too.
    accountChooser.filter.addEventListener("change", () => {
      paintClosingDateField();
    });
    paintClosingDateField();
    // A month whose books list no account that can take a statement says so in
    // words; the chooser's own empty copy is about the chart, which has names
    // (rd13 L1, review round 2).
    if (load.status === "ok" && eligible.length === 0) {
      accountHost.append(
        h("p", { class: "muted", "data-role": "statement-accounts-empty" }, [
          noStatementAccountText(period),
        ]),
      );
    }
    accountHost.append(
      requestSlot("account", load, { role, chooser: accountChooser }),
    );
  };

  /**
   * Which accounts the ledger says are bank, card or wallet accounts (rd13 L1).
   * A statement is a bank's document, so `Assets:RENT:Due-From:TRST` is not on
   * the list. Undefined when the read failed, which keeps the older
   * balance-sheet list rather than offering nothing at all.
   */
  /**
   * The page's ONE ledger read (rd13 L1): the account list's kinds and the
   * cards' books figures are the same endpoint for the same month, so one
   * successful answer serves both. A FAILED read is not kept, so the next
   * paint asks again; a write that can move the books clears the answers
   * (`forgetBooksRead`).
   */
  const ledgerAnswers = new Map<string, Awaited<ReturnType<AdminClient["getLedgerAccounts"]>>>();
  const readLedger = async (
    forPeriod: string,
  ): Promise<Awaited<ReturnType<AdminClient["getLedgerAccounts"]>>> => {
    const known = ledgerAnswers.get(forPeriod);
    if (known !== undefined) return known;
    const response = await client.getLedgerAccounts(company, forPeriod);
    ledgerAnswers.set(forPeriod, response);
    return response;
  };

  const readAccountKinds = async (): Promise<
    ReadonlyMap<string, StatementAccountKind> | undefined
  > => {
    try {
      const response = await readLedger(period);
      return new Map(
        response.accounts.map((row) => [
          row.account,
          { type: row.type, isCash: row.isCash },
        ]),
      );
    } catch {
      return undefined;
    }
  };

  /**
   * The picker paints when BOTH facts it needs have settled: the chart (which
   * accounts exist) and the ledger's own kinds (which of them can have a
   * statement). It never paints on the wide assets-and-liabilities list while
   * the read is still out — that list is only for a read that FAILED, or the
   * quiet intercompany account would be offered for as long as the read takes
   * (round 13, review round 1).
   */
  let chartReady: ChartLoadState | null = null;
  let kindsSettled = false;
  /**
   * Which month the kinds being read belong to. A read that comes back after
   * the person moved on must not paint its month's list (rd13 L1, review
   * round 2).
   */
  let kindsEpoch = 0;
  const paintPickerIfReady = (): void => {
    if (chartReady === null || !kindsSettled) return;
    paintAccountChooser(chartReady);
  };

  /**
   * Read the chosen month's account kinds and repaint the picker. The ledger
   * read itself is cached per month (`readLedger`), so moving back and forth
   * costs one read per month, and pressing the control again does not re-ask.
   */
  const reloadAccountKinds = (): void => {
    const epoch = ++kindsEpoch;
    kindsSettled = false;
    // The kinds are a second question to the same ledger read, and they must
    // NOT gate the page: a slow or failed books read would otherwise hold
    // every confirm card behind it. Only the picker waits for them.
    void readAccountKinds().then((kinds) => {
      if (!stillCurrent() || epoch !== kindsEpoch) return;
      accountKinds = kinds;
      kindsSettled = true;
      paintPickerIfReady();
    });
  };

  reloadAccountKinds();

  void (async () => {
    try {
      if (!stillCurrent()) return;
      chartReady = await client.getChartSnapshot(company).then(
        (snapshot) => chartLoadFromSnapshot(snapshot),
        () => chartLoadFromError(company),
      );
      if (!stillCurrent()) return;
      paintPickerIfReady();
    } catch {
      // A chart read that failed is said in the chooser's own words; the form
      // stays usable, and `chartSettled` still unblocks the cards.
      chartReady = chartLoadFromError(company);
      if (stillCurrent()) paintPickerIfReady();
    } finally {
      chartSettled();
    }
  })();

  const processingHost = h("div", {
    "data-role": "statement-processing",
    hidden: "true",
  });
  root.append(processingHost);

  const confirmHost = h("div", { "data-role": "statement-confirmations" });
  root.append(confirmHost);

  const sessionHost = h("div", { "data-role": "session-uploads" });
  root.append(sessionHost);

  const paintSession = (): void => {
    clear(sessionHost);
    const entries = listSessionUploads();
    sessionHost.append(
      h("h2", { class: "section-title" }, ["Uploaded this session"]),
    );
    if (entries.length === 0) {
      sessionHost.append(
        h("p", { class: "muted empty-state", "data-role": "session-uploads-empty" }, [
          "No uploads in this session yet. Choose an account and a PDF, then use Upload PDF.",
        ]),
      );
      return;
    }
    const list = h("ul", { class: "session-upload-list", "data-role": "session-upload-list" });
    const now = new Date();
    for (const entry of entries) {
      // The page only knows what the Worker answered for the month on screen
      // (rd16 U review round 2, item 3). Another month's upload, or a month
      // whose read has not painted yet, gets no waiting/late claim at all.
      const rowsKnown =
        rowsPainted && entry.company === company && entry.period === period;
      list.append(
        renderSessionRow(entry, dir, now, {
          rowsKnown,
          checked: rowsKnown && statementUploadIsChecked(entry, paintedRows),
        }),
      );
    }
    sessionHost.append(list);
  };
  paintSession();

  /**
   * The cards already on screen, by reconciliation id (rd round 11, R7-09).
   *
   * A poll paints the same rows every five seconds. Rebuilding them cleared
   * whatever the owner had typed, and replaced a card whose Confirm was in
   * flight. A card is therefore reused unless its row materially changed AND
   * nothing on it is in use; a busy card is never rebuilt.
   *
   * Round 2 (MUST-FIX 2): a card the owner HAS typed into is no longer pinned
   * to the old row forever. When the row changes, it is rebuilt from the new
   * row and "This card changed. Check the new figures." is shown, so Confirm
   * posts the NEW fingerprint. Before this, a newer statement left the card on
   * the old fingerprint and Confirm looped on `statement_changed`.
   *
   * Round 3 (MUST-FIX 1): a NEW STATEMENT FINGERPRINT builds the card FRESH
   * from the new row — no carried typing, no carried prefill, no lock. A figure
   * belongs to the statement it was typed for (the orchestrator withdrew the
   * round-1 carry-across). Carrying typed values is allowed only while the
   * fingerprint is unchanged, which is the R7-09 poll repaint.
   */
  interface ConfirmCardState {
    row: StatementReconRow;
    element: HTMLElement;
    signature: string;
    dirty: boolean;
    busy: boolean;
    done: boolean;
    doneMessage: string | null;
    period: string;
    /** A success whose figure is still to be read back from the refreshed row. */
    awaitingStoredFigure: boolean;
    /** The values a failed dispatch locked in; a rebuild must keep them locked. */
    locked: TypedConfirmFields | null;
    /**
     * The statement fingerprint this card's Confirm sent. A done card may only
     * stand while the newest row is still `auto_confirmed` with THIS fingerprint
     * (round 3, MUST-FIX 2).
     */
    confirmedFingerprint: string | null;
    /**
     * The card's transient status line (round 4). `null` on a done card, whose
     * status IS its finished sentence. A card that leaves the screen and comes
     * back must not resume saying "Checking whether it saved…" over a Confirm
     * button that is live again, so the line is reset on re-attach.
     */
    status: HTMLElement | null;
    /**
     * Moves when a repaint resets this card's status line, which is the page
     * saying the press it described is over (round 4). A press whose reads are
     * still out compares this before writing, so a late verdict cannot land on
     * a card that has already been reset and redrawn.
     */
    pressEpoch: number;
  }
  const confirmCards = new Map<string, ConfirmCardState>();
  let paintedPeriod: string | null = null;

  /**
   * The state the card's own content is built from: the WHOLE row minus an
   * explicit denylist (`updatedAt` changes on every write and does not change
   * what the card says), plus the books figure a confirmation moves and the
   * session-upload evidence that licenses "being checked" (round 2,
   * SHOULD-FIX 6). Anything else is a real change to the card, not a repaint.
   */
  const rowSignature = (row: StatementReconRow): string =>
    statementCardSignature(row, {
      books: row.outcome === "mismatch" ? booksFor(row) : null,
      sessionUpload: sessionUploadForAccount(row),
      matchedUpload: sessionUploadFor(row),
    });

  const cardHasFocus = (element: HTMLElement): boolean =>
    element.contains(document.activeElement);

  /** The typed Confirm fields still on a card, or null when it draws none. */
  const typedValuesIn = (element: HTMLElement): TypedConfirmFields | null => {
    const read = (role: string): HTMLInputElement | null =>
      element.querySelector<HTMLInputElement>(`[data-role="${role}"]`);
    const mask = read("confirm-mask");
    const balance = read("confirm-balance");
    const marker = read("confirm-marker");
    const cue = read("confirm-cue");
    if (mask === null || balance === null || marker === null || cue === null) return null;
    return {
      mask: mask.value,
      balance: balance.value,
      marker: marker.value,
      cue: cue.value,
    };
  };

  /** Bring a host's children to `nodes`, moving only what is out of place. */
  const syncChildren = (host: HTMLElement, nodes: readonly Node[]): void => {
    const wanted = new Set<Node>(nodes);
    let cursor = host.firstChild;
    for (const node of nodes) {
      if (cursor === node) {
        cursor = cursor.nextSibling;
        continue;
      }
      host.insertBefore(node, cursor);
    }
    let rest = cursor;
    while (rest !== null) {
      const next = rest.nextSibling;
      if (!wanted.has(rest)) host.removeChild(rest);
      rest = next;
    }
  };

  const cardFor = (row: StatementReconRow): HTMLElement => {
    const existing = confirmCards.get(row.id);
    const signature = rowSignature(row);
    if (existing !== undefined) {
      // A write in flight owns the card: never replace it under the press.
      if (existing.busy) return existing.element;
      if (existing.done) {
        // The done card is refreshed from the newest row, so its sentence comes
        // from the figure the server stored (round 2, MUST-FIX 1) and its
        // fingerprint is the one a future read names.
        if (!existing.awaitingStoredFigure && existing.signature === signature) {
          return existing.element;
        }
        const stored = row.statementBalanceDecimal;
        const message =
          existing.awaitingStoredFigure && stored !== null
            ? confirmedBalanceSentence(stored)
            : (existing.doneMessage ?? confirmedBalanceSentence(stored));
        const rebuilt = renderConfirmCard(row, message, {
          confirmedFingerprint: existing.confirmedFingerprint,
        });
        rebuilt.awaitingStoredFigure = existing.awaitingStoredFigure && stored === null;
        confirmCards.set(row.id, rebuilt);
        return rebuilt.element;
      }
      if (existing.signature === signature) return existing.element;
      // A DIFFERENT STATEMENT is a different figure (round 3, MUST-FIX 1). The
      // card is built fresh from the new row: the typed values, the prefill and
      // the lock belong to the statement they were typed for, and the person
      // enters or confirms the balance again. Carrying them across posted the
      // old statement's figure against the new statement's fingerprint.
      if (rowFingerprint(existing.row) !== rowFingerprint(row)) {
        const rebuilt = renderConfirmCard(row, null, { changed: true });
        confirmCards.set(row.id, rebuilt);
        return rebuilt.element;
      }
      if (existing.dirty || cardHasFocus(existing.element)) {
        // Same statement, changed card: the owner's typing is still their
        // unsaved input for the same figure, so it survives the poll repaint
        // (R7-09), and the card says plainly that the card underneath changed.
        const carried = typedValuesIn(existing.element);
        const rebuilt = renderConfirmCard(row, null, {
          carried,
          locked: existing.locked,
          changed: true,
        });
        confirmCards.set(row.id, rebuilt);
        return rebuilt.element;
      }
      // A finished Confirm is rebuilt with the newest figures, and its success
      // sentence is carried into the new card instead of being lost
      // (rd round 11, R7-22a).
    }
    const state = renderConfirmCard(row, existing?.doneMessage ?? null, {
      locked: existing?.locked ?? null,
    });
    confirmCards.set(row.id, state);
    return state.element;
  };

  const pendingHeading = h(
    "h2",
    { class: "section-title", "data-role": "confirm-section-title" },
    ["Needs confirmation"],
  );
  const waitingHeading = h(
    "h2",
    { class: "section-title", "data-role": "publication-waiting-title" },
    ["Waiting to be saved to the books"],
  );
  const savedHeading = h(
    "h2",
    { class: "section-title", "data-role": "publication-section-title" },
    ["Saved to the books"],
  );

  const paintConfirmations = (items: StatementReconRow[]): void => {
    const byId = new Map(items.map((row) => [row.id, row]));
    // A card with a finished sentence is DONE: it is compact, sentence-only,
    // and lives outside the pending heading (round 2, MUST-FIX 3). Refresh its
    // element from the newest row first, so the sentence names the stored
    // figure and the card is current.
    //
    // Round 3 (MUST-FIX 2): a done card only stands while the row is STILL
    // `auto_confirmed` with the fingerprint that confirm sent. A newer statement
    // upload, or a row that went back to `needs_review`/`mismatch`, means the
    // sentence no longer describes the row: it is dropped and the pending card
    // is drawn instead. Otherwise a finished card hid the statement that
    // replaced it.
    const doneIds = new Set<string>();
    for (const [id, state] of confirmCards) {
      if (!state.done) continue;
      const newest = byId.get(id);
      if (state.period !== paintedPeriod) continue; // dropped by the loop below
      if (
        !confirmRowMatchesDone(
          newest ?? null,
          state.confirmedFingerprint,
        )
      ) {
        confirmCards.delete(id);
        continue;
      }
      doneIds.add(id);
      if (newest !== undefined) cardFor(newest);
    }
    const pending = items.filter(
      (row) =>
        (row.outcome === "needs_review" ||
          row.outcome === "mismatch" ||
          row.publicationState === "stale" ||
          row.publicationState === "blocked") &&
        !doneIds.has(row.id),
    );
    // A done card already records its own account, so the publication status
    // for the same row would say it twice.
    const savedRows = items.filter(
      (row) => row.publicationState === "published" && !doneIds.has(row.id),
    );
    const waitingRows = items.filter(
      (row) =>
        (row.publicationState === "awaiting_publication" ||
          row.publicationState === "calculated") &&
        !doneIds.has(row.id),
    );

    const pendingCards = pending.map((row) => cardFor(row));
    const used = new Set(pending.map((row) => row.id));
    const doneCards: HTMLElement[] = [];
    // A finished Confirm keeps its sentence (rd round 11, R7-22a) and an
    // in-flight one keeps its card, even when the row has moved groups or
    // left the list between two polls.
    for (const [id, state] of confirmCards) {
      if (state.period !== paintedPeriod) {
        confirmCards.delete(id);
        continue;
      }
      if (used.has(id)) continue;
      if (state.busy) {
        pendingCards.push(state.element);
        used.add(id);
      } else if (state.done) {
        doneCards.push(state.element);
        used.add(id);
      } else {
        confirmCards.delete(id);
      }
    }

    // Each heading is true of every row under it: a row that is not saved never
    // sits under "Saved to the books" (re-check H4), so the two states are two
    // groups and the heading is chosen from the row's own state. Done cards
    // come after the pending group and under no pending heading: the work is
    // finished, so it is not awaiting confirmation.
    const children: Node[] = [];
    if (pendingCards.length > 0) children.push(pendingHeading, ...pendingCards);
    children.push(...doneCards);
    if (waitingRows.length > 0) {
      children.push(waitingHeading, ...waitingRows.map(renderPublicationStatus));
    }
    if (savedRows.length > 0) {
      children.push(savedHeading, ...savedRows.map(renderPublicationStatus));
    }
    // The error state clears this host, so the group nodes are re-attached by
    // the next successful paint; syncChildren keeps reused cards intact.
    //
    // A card whose status line was written for one press can leave the screen
    // (a failed paint clears the host) and come back (the Try again that
    // followed). That line describes the press, not the list, so it is cleared
    // when the card is re-attached: a stale "Checking whether it saved…" under a
    // Confirm button that is live again is exactly the defect round 4 removes
    // (MUST-FIX 1). A done card is exempt — its status IS its finished sentence
    // — and a busy press keeps its line, because its read is still coming.
    const attachedBefore = new Set<HTMLElement>();
    for (const state of confirmCards.values()) {
      if (state.element.parentNode === confirmHost) attachedBefore.add(state.element);
    }
    syncChildren(confirmHost, children);
    for (const state of confirmCards.values()) {
      if (state.busy || state.done || state.status === null) continue;
      if (attachedBefore.has(state.element)) continue;
      if (state.element.parentNode !== confirmHost) continue;
      clear(state.status);
      // The line went with the press that wrote it. A verdict still in flight
      // must not land on the card that replaced it (round 5, MUST-FIX 1 keeps
      // the press from writing a stale line after the card is redrawn).
      state.pressEpoch += 1;
    }
  };

  /**
   * The books' closing balance for one period, and which period it is for
   * (rd Z4 C-11). The mismatch sentence names three figures and each must say
   * whose it is; the bank's figure is never offered as the books' figure.
   *
   * Read only when a row on this period is a mismatch whose own date is the
   * books' date (round-2 MUST-FIX 1 and 6): a month with no mismatch costs
   * nothing. `balances: null` means the read failed. The period is stamped on
   * the value, so a read that lands after the month changed is dropped rather
   * than shown beside another month's row (round-2 MUST-FIX 5a).
   *
   * A FAILED read is kept only for the paint that made it, never reused: it is
   * an answer about one attempt, not a fact about the month, and the ledger may
   * have published a moment later (round-3 MUST-FIX 1). Any write that moves
   * the books — Confirm, or a statement that settles and is published — drops
   * the figure, so what is printed always comes from the newest snapshot.
   */
  let booksRead: { period: string; balances: ReadonlyMap<string, string> | null } | null = null;

  /** Forget the month's books answer; the next paint reads it again. */
  const forgetBooksRead = (): void => {
    booksRead = null;
    ledgerAnswers.clear();
  };

  /**
   * True only when the answer in hand can be printed for this period: one
   * period's SUCCESSFUL read. A failure is never "in hand".
   */
  const booksAnswerInHand = (forPeriod: string): boolean =>
    booksRead !== null && booksRead.period === forPeriod && booksRead.balances !== null;

  const readBooksBalances = async (
    forPeriod: string,
  ): Promise<ReadonlyMap<string, string> | null> => {
    try {
      const response = await readLedger(forPeriod);
      return new Map(response.accounts.map((row) => [row.account, row.closing]));
    } catch {
      return null;
    }
  };

  /** The books figure for one row, or no claim about the books at all. */
  const booksFor = (row: StatementReconRow): BooksFigure => {
    if (booksRead === null || booksRead.period !== row.period) {
      return { books: null, booksReadFailed: false };
    }
    if (booksRead.balances === null) {
      return { books: null, booksReadFailed: true };
    }
    return booksFigureFor({
      account: row.account,
      closingDate: row.closingDate,
      period: row.period,
      balances: booksRead.balances,
    });
  };

  const needsBooks = (items: readonly StatementReconRow[], forPeriod: string): boolean =>
    items.some(
      (row) =>
        row.outcome === "mismatch" &&
        row.period === forPeriod &&
        booksShareStatementDate(row.closingDate, forPeriod),
    );

  /**
   * Every reconciliation read takes a monotonic token, and only the newest
   * token may paint. Without this, a read that was held (slow month, slow
   * books figure) could land AFTER a newer read and repaint older rows over it
   * (round 2, MUST-FIX 4).
   */
  let latestReadToken = 0;
  const beginRead = (): number => {
    latestReadToken += 1;
    return latestReadToken;
  };
  const isNewestRead = (token: number): boolean => token === latestReadToken;

  /**
   * The rows the newest paint actually drew, and a counter that moves once per
   * paint (round 5, MUST-FIX 1). A failed Confirm has two reads racing: the one
   * it asks itself, and the repaint of the list. The paint is started second,
   * so its answer is the newer observation — and a write that lands between the
   * two reads is exactly what made the card say "Nothing was saved" over a list
   * that showed the row saved. Recording the painted rows lets the verdict use
   * the newer answer without waiting on any particular read's promise (which a
   * held read never settles, and a Refresh overtakes).
   */
  let paintSequence = 0;
  let lastPainted: { rows: StatementReconRow[]; seq: number } | null = null;
  // Woken by the next paint, so the verdict follows a paint that lands at once
  // without polling for it.
  const paintWaiters = new Set<() => void>();
  const notePaint = (rows: StatementReconRow[]): void => {
    paintSequence += 1;
    lastPainted = { rows, seq: paintSequence };
    for (const wake of [...paintWaiters]) wake();
  };

  /**
   * Paint the cards for one period, reading the books figures first when at
   * least one mismatch could use one. A read already in hand for this period is
   * reused, so the upload poll does not ask for it again every five seconds,
   * but a read that FAILED is asked again on the next paint (round-3
   * MUST-FIX 1): the ledger may have published since.
   */
  const paintRecons = async (
    items: StatementReconRow[],
    forPeriod: string,
    token: number,
  ): Promise<void> => {
    if (needsBooks(items, forPeriod) && !booksAnswerInHand(forPeriod)) {
      const balances = await readBooksBalances(forPeriod);
      if (!stillCurrent() || period !== forPeriod || !isNewestRead(token)) return;
      booksRead = { period: forPeriod, balances };
    }
    if (!stillCurrent() || period !== forPeriod || !isNewestRead(token)) return;
    if (paintedPeriod !== null && paintedPeriod !== forPeriod) {
      // Cards belong to the month they were read for; a month change starts
      // with no carried-over typed state.
      confirmCards.clear();
    }
    paintedPeriod = forPeriod;
    paintedRows = items;
    rowsPainted = true;
    // The day Books has for a card and month is on the upload field's hint, so
    // the paint that just read the month also refreshes it.
    paintClosingDateField();
    paintConfirmations(items);
    // A session row's "waiting / late" sentence depends on whether the Worker
    // has since answered (rd16 U review, item 1), so the list is repainted with
    // the rows this paint just read. The list holds no input or focus, unlike a
    // confirm card, so rebuilding it is safe.
    paintSession();
    // An account a reconciliation row names is recognised whatever its name
    // looks like (rd16 U review round 2, item 6). When that set grows, the two
    // bands are repainted once so the account moves into "Bank and card
    // accounts"; the chooser keeps the chosen account across the repaint.
    if (statementAccountNames().some((name) => !paintedRecognition.includes(name))) {
      paintPickerIfReady();
    }
    // The rows this paint drew are now the newest answer the page holds about
    // the month (round 5, MUST-FIX 1). Recorded here, after the cards have been
    // drawn, so a paint that was refused or overtaken never counts.
    notePaint(items);
    // The list has painted, even when it paints nothing. A reader that counts
    // this page's reads waits for this marker, because a count taken before the
    // page has loaded would prove nothing (round-3 MUST-FIX 3).
    confirmHost.setAttribute("data-settled", "true");
  };

  /**
   * Read the month and paint its cards. Returns the rows that were painted, or
   * `null` when the read failed or was overtaken. The rows it returns are only
   * good for a paint decision: the failed-Confirm question "did it save?" is
   * answered by {@link readDecidingRows}, because an answer that may be dropped
   * as stale is not an answer (round 4, MUST-FIX 1).
   */
  const refreshRecons = async (): Promise<StatementReconRow[] | null> => {
    const forPeriod = period;
    if (!stillCurrent() || !isValidPeriod(forPeriod)) return null;
    const token = beginRead();
    // The marker means "this paint finished"; a new read is starting, so it is
    // not true again until the list has painted. The session list's waiting/late
    // sentence goes quiet with it (rd16 U review round 2, item 3).
    rowsPainted = false;
    confirmHost.removeAttribute("data-settled");
    try {
      const { items } = await client.listStatementReconciliations(company, forPeriod);
      await chartRead;
      if (!stillCurrent() || period !== forPeriod || !isNewestRead(token)) return null;
      lastLoadedAt = new Date();
      paintFreshness();
      await paintRecons(items, forPeriod, token);
      return items;
    } catch (error) {
      console.error("statement reconciliations load failed", error);
      if (!stillCurrent() || period !== forPeriod || !isNewestRead(token)) return null;
      clear(confirmHost);
      const retry = button("Try again", "primary", {
        "data-action": "retry-statement-checks",
        "aria-label": "Try again",
      });
      retry.addEventListener("click", () => {
        void refreshRecons();
      });
      confirmHost.append(
        h("div", { class: "error-state", role: "alert", "data-role": "statement-recon-error" }, [
          h("p", {}, ["Couldn't load statement checks for this month. Try again."]),
          retry,
        ]),
      );
      return null;
    }
  };

  /**
   * The read that decides whether a failed Confirm stored anything.
   *
   * It is deliberately NOT {@link refreshRecons}: that read joins the shared
   * monotonic token, so a Refresh pressed while the card says "Checking whether
   * it saved…" becomes the newest read, and the failed press's read is then
   * dropped as stale — the verdict never lands and "Checking…" stays on screen
   * beside a Confirm button that is live again (round 4, MUST-FIX 1). This one
   * calls the client directly, paints nothing and takes no token, so it answers
   * even when a newer paint has already won. It reads the month the pressed row
   * belongs to, not whatever month is on screen when the answer arrives.
   *
   * It is not the only answer, though: the repaint of the list asks the same
   * question a moment later, so it is the newer of the two and the verdict
   * prefers it (round 5, MUST-FIX 1). This read is the fallback for a paint that
   * is refused, held or overtaken.
   */
  const readDecidingRows = async (forPeriod: string): Promise<StatementReconRow[] | null> => {
    try {
      const { items } = await client.listStatementReconciliations(company, forPeriod);
      return items;
    } catch (error) {
      // A failed read is not an answer: the caller says so and keeps Confirm.
      console.error("statement reconciliations read failed", error);
      return null;
    }
  };

  /**
   * How long the card waits for the newer paint before falling back to the read
   * it asked itself (round 5, MUST-FIX 1). A paint is one quick read, so this
   * only bounds one that is held, refused or overtaken: the card may not sit on
   * "Checking whether it saved…" for ever because a repaint never came back.
   */
  const PAINT_VERDICT_WAIT_MS = 800;

  /**
   * The rows of the newest paint that finished after `afterSeq`, or `null` when
   * none did inside {@link PAINT_VERDICT_WAIT_MS}.
   *
   * Waiting on the paint read's own promise would hang in two real cases: a
   * Refresh pressed from the checking card overtakes that read (so its answer
   * is dropped as stale and never resolves as the newest), and a held read never
   * resolves at all. Watching the paint SEQUENCE instead means a newer paint
   * answers for a stalled older one, and the bound means no paint at all still
   * ends the press (round 5, MUST-FIX 1).
   */
  const newestPaintedRows = async (
    afterSeq: number,
    paint: Promise<StatementReconRow[] | null>,
  ): Promise<StatementReconRow[] | null> => {
    if (lastPainted !== null && lastPainted.seq > afterSeq) return lastPainted.rows;
    // Woken by the next paint, by the paint this press started concluding
    // WITHOUT painting (refused or overtaken — then the bound is not waited
    // out), or by the bound itself for a paint that never settles at all.
    const nextPaint = (): Promise<void> =>
      new Promise<void>((resolve) => {
        let timer: ReturnType<typeof setTimeout> | null = null;
        const finish = (): void => {
          if (timer !== null) {
            clearTimeout(timer);
            timer = null;
          }
          paintWaiters.delete(finish);
          resolve();
        };
        timer = setTimeout(finish, PAINT_VERDICT_WAIT_MS);
        paintWaiters.add(finish);
      });
    await Promise.race([paint.then(() => undefined), nextPaint()]);
    if (lastPainted !== null && lastPainted.seq > afterSeq) return lastPainted.rows;
    return null;
  };
  void refreshRecons();

  /**
   * One watch per uploaded (account, period) (rd round 11, R7-08).
   *
   * The poll used to stop watching when the page month changed, which left its
   * banner on screen forever, and a second upload overwrote the first one's
   * banner. Each watch is now keyed by what was uploaded, so it settles on its
   * own and removes only its own line.
   */
  interface UploadWatch {
    key: string;
    account: AccountPath;
    period: string;
  }
  const watches = new Map<string, UploadWatch>();

  const paintProcessing = (): void => {
    processingHost.hidden = watches.size === 0;
    clear(processingHost);
    for (const watch of watches.values()) {
      processingHost.append(
        h(
          "p",
          {
            class: "status",
            role: "status",
            "data-role": "processing-status",
            "data-watch-key": watch.key,
          },
          [
            // The account's own name, never its ledger path (rd Z4 C-11).
            processingSentence(accountLabelBeside(watch.account, offeredAccounts(), companies)),
          ],
        ),
      );
    }
  };

  const startProcessing = (watch: UploadWatch): void => {
    watches.set(watch.key, watch);
    paintProcessing();
  };

  const hideProcessing = (key: string): void => {
    watches.delete(key);
    paintProcessing();
  };

  const pollUntilSettled = async (watch: UploadWatch): Promise<void> => {
    try {
      await pollStatement(watch);
    } finally {
      hideProcessing(watch.key);
      if (stillCurrent()) {
        // Processing has settled (or this page stopped watching): what the
        // statement changed must not be served from the read cache (rd-x2).
        settleReads(client);
        // A statement that settles may have been published, which moves the
        // ledger's closing balance: the figure the poll read is no longer this
        // month's truth. Drop it, then read and paint again (round-3 MUST-FIX 1).
        forgetBooksRead();
        if (period === watch.period) await refreshRecons();
      }
    }
  };

  /**
   * Watch one uploaded (account, period) until its reconciliation names the
   * file.
   *
   * An upload nulls the row's binding and sets needs_review until the
   * statements workflow restamps it, so "a row exists" is NOT "the check is
   * done": stopping there is what painted a resting card over a statement
   * that was still being checked (rd Z8 round 2, MUST-FIX 1). Keep polling
   * until the binding appears or the poll limit is reached.
   *
   * The watch follows its own upload even when the owner moves to another
   * month; it paints only the month on screen.
   */
  const pollStatement = async (watch: UploadWatch): Promise<void> => {
    const started = Date.now();
    startProcessing(watch);
    while (Date.now() - started < POLL_MAX_MS) {
      if (!stillCurrent()) return;
      const token = beginRead();
      try {
        const { items } = await client.listStatementReconciliations(company, watch.period);
        if (!stillCurrent()) return;
        // A held poll read must not paint over the newer read that overtook it.
        if (period === watch.period && isNewestRead(token)) {
          await paintRecons(items, watch.period, token);
        }
        const match = items.find((row) => row.account === watch.account);
        if (match !== undefined && rowFingerprint(match) !== null) {
          return;
        }
      } catch {
        // Keep polling — Action may still be starting.
      }
      await sleep(POLL_MS);
    }
    if (!stillCurrent() || period !== watch.period) return;
    clear(status);
    status.className = "status";
    status.append(document.createTextNode(processingTimeoutCopy(role)));
  };

  function renderConfirmCard(
    row: StatementReconRow,
    doneMessage: string | null = null,
    options: {
      /** Values the owner typed, carried into a rebuilt card (round 2). */
      carried?: TypedConfirmFields | null;
      /** Values a failed write locked in; a rebuild must keep them locked. */
      locked?: TypedConfirmFields | null;
      /** This card replaced one whose row changed under the owner. */
      changed?: boolean;
      /** The fingerprint a done card carried over from the card it replaced. */
      confirmedFingerprint?: string | null;
    } = {},
  ): ConfirmCardState {
    // A finished Confirm is a compact, sentence-only card: no fields and no
    // second press, and it is never rendered under "Needs confirmation"
    // (round 2, MUST-FIX 3).
    if (doneMessage !== null) {
      return renderDoneConfirmCard(
        row,
        doneMessage,
        options.confirmedFingerprint ?? null,
      );
    }
    const carried = options.carried ?? null;
    let locked = options.locked ?? null;
    const maskInput = h("input", {
      type: "text",
      class: "input",
      "aria-label": "Account last 4",
      "data-role": "confirm-mask",
      maxlength: "4",
      placeholder: "0000",
    });
    // rd13 L4 item 2: on a mismatch the box starts EMPTY, because the figure
    // read from the statement is the one the check says disagrees. Prefilling
    // it made the double check answer itself in one press — "Confirmed an
    // ending balance of $584.00" over "the statement shows $584.00". A carried
    // value is the owner's own unsaved typing for the same statement, so it is
    // kept (round 3's repaint rule).
    const mismatch = row.outcome === "mismatch";
    const balanceInput = h("input", {
      type: "text",
      class: "input",
      "aria-label": "Confirmed ending balance",
      "data-role": "confirm-balance",
      // Only a figure READ FROM THE STATEMENT is filled in, and never on a
      // mismatch. The bank's figure is the other side of the comparison:
      // filling it in made one press of Confirm post the bank's number as the
      // statement's (round eight A-H3).
      value: carried?.balance ?? (mismatch ? "" : row.statementBalanceDecimal ?? ""),
    });
    const hintId = `confirm-hint-${row.id.replace(/[^A-Za-z0-9_-]/g, "")}`;
    const markerInput = h("input", {
      type: "text",
      class: "input",
      "aria-label": "Account marker pattern",
      "data-role": "confirm-marker",
    });
    // Default marker uses a placeholder last-4; AM overwrites with the real mask.
    markerInput.value = carried?.marker ?? "ending in";
    const cueInput = h("input", {
      type: "text",
      class: "input",
      "aria-label": "Balance cue label",
      "data-role": "confirm-cue",
      value: carried?.cue ?? "ending balance",
    });
    maskInput.value = carried?.mask ?? "";
    const cardStatus = h("p", {
      class: "status",
      role: "status",
      "data-role": "confirm-status",
    });
    // The line the R7-07 lock adds when a failed write has already stored the
    // figure (round 2, SHOULD-FIX 9). Empty until the lock is on.
    const lockedLine = h("p", {
      class: "muted",
      "data-role": "confirm-locked",
    });
    // The row changed under typed input: say so, and say what to do (round 2,
    // MUST-FIX 2).
    const changedNotice = options.changed
      ? h("p", { class: "status banner-warn", "data-role": "confirm-changed" }, [
          "This card changed. Check the new figures.",
        ])
      : null;
    const confirmBtn = button("Confirm", "primary", {
      "data-action": "confirm-statement",
      "aria-label": "Confirm statement",
    });
    // Confirm waits for a figure. While the box is empty the sentence above it
    // says why, and the button points at that sentence.
    //
    // rd13 L4 item 2: on a mismatch the press stays available with an empty box,
    // so the press itself answers with MISMATCH_EMPTY_CONFIRM_COPY and changes
    // nothing; a disabled button would leave an emptied box with no sentence at
    // all. Every other card keeps the disabled-until-typed behaviour.
    const emptyCanBePressed = mismatch;
    let confirming = false;
    const syncConfirm = (): void => {
      if (
        confirming ||
        (!emptyCanBePressed && balanceInput.value.trim() === "")
      ) {
        confirmBtn.setAttribute("disabled", "true");
      } else {
        confirmBtn.removeAttribute("disabled");
      }
    };
    balanceInput.addEventListener("input", syncConfirm);
    // Point at the hint only when the card draws one (rd13 L4 review round 1:
    // an unbound row draws none, and a dangling id describes nothing).
    if (rowFingerprint(row) !== null && (row.statementBalanceDecimal === null || mismatch)) {
      confirmBtn.setAttribute("aria-describedby", hintId);
    }
    syncConfirm();

    // The Worker lets only the owner confirm a statement, so the account
    // manager is shown what is waiting and who answers it, never a form that
    // always ends in "Could not confirm" (re-check M1). The role is read when
    // the card is drawn, not when the page opened.
    const ownerOnly = role === "account_manager";
    // Which statement this card is about, in words the owner can check. The
    // fingerprint names the exact file; when this session uploaded that file,
    // the card can also say when (rd Z8: the owner is vouching for a file).
    const fingerprint = rowFingerprint(row);
    // `null` AND a missing/empty fingerprint are one state (rd Z8 round 2,
    // MUST-FIX 3): the read has to name a file before this card may send one.
    // A stale or hand-made row without the field is not a reason to draw a form
    // whose every press ends in a refusal.
    const unbound = fingerprint === null;
    const upload = fingerprint === null ? null : sessionUploadFor(row);
    // Evidence that a check is actually running (rd Z8 round 3, MUST-FIX 1): this
    // session uploaded a statement for this account and month. A null binding on
    // its own proves nothing — the Plaid-only row stays null forever.
    const uploadedThisSession = fingerprint === null ? sessionUploadForAccount(row) : null;
    const unboundCopy =
      uploadedThisSession === null ? STATEMENT_NOT_CHECKED_COPY : STATEMENT_CHECKING_COPY;
    // The heading states the same fact as the sentence below it: with no
    // evidence of a check, "Being checked" is the same false claim (rd Z8
    // round 3, MUST-FIX 1).
    const unboundHeading =
      uploadedThisSession === null ? "Not checked yet" : "Being checked";
    // Only a row that names a file may say there is one on file.
    const statementFileLine =
      fingerprint === null
        ? []
        : [
            h("p", { "data-role": "confirm-statement-file" }, [
              "The statement on file for this account and month.",
            ]),
            ...(upload === null
              ? []
              : [
                  h("p", { class: "muted", "data-role": "confirm-statement-uploaded" }, [
                    `Uploaded this session at ${formatSessionTime(upload.uploadedAt)}.`,
                  ]),
                ]),
          ];
    // No binding is the just-uploaded state too: every upload nulls the
    // binding and sets needs_review until the statements workflow restamps it,
    // so the card says the file is being checked and the poll keeps going
    // (rd Z8 round 2, MUST-FIX 1). "Upload the statement again" is said only
    // when the Worker answers `no_statement` (see STATEMENT_NO_FILE_COPY), and
    // "being checked" only with session-upload evidence (round 3, MUST-FIX 1).
    //
    // A card that cannot name a file prints no figure either: a balance still
    // on the row came from the statement that was replaced, and this card must
    // not attribute it to the file being checked now.
    const figures: Node[] = [];
    const closingDateAdvice = cardMismatchClosingDateAdvice(row.account);
    if (!unbound) {
      if (row.outcome === "mismatch") {
        const books = booksFor(row);
        // rd13 L4 item 1: a way to SEE the account's transactions for the
        // period the gap is in. Books' register reads `account` and `period`
        // from the nav bus (pages/general-ledger.ts takeNavParams), so the
        // press lands on that account's entries for this month. Activity does
        // not read nav params, so pointing there would promise a filter it
        // does not apply.
        const entriesLabel = `See this account's transactions for ${monthText(row.period)}`;
        const entriesLink = button(entriesLabel, "secondary", {
          "data-action": "see-mismatch-entries",
          "aria-label": entriesLabel,
        });
        entriesLink.addEventListener("click", () => {
          navigateTo("general-ledger", { account: row.account, period: row.period });
        });
        figures.push(
          // The gap first: how far apart, which way, and on what date. The
          // three labelled figures stay under it as the evidence for the gap.
          h("div", { "data-role": "mismatch-difference" }, [
            h("p", {}, [
              mismatchDifferenceCopy({
                bank: row.plaidBalanceDecimal,
                statement: row.statementBalanceDecimal,
                closingDate: row.closingDate,
                period: row.period,
                role,
              }),
            ]),
          ]),
          h("div", { "data-role": "mismatch-figures" }, [
            h("p", {}, [
              mismatchFiguresCopy({
                bank: row.plaidBalanceDecimal,
                statement: row.statementBalanceDecimal,
                books: books.books,
                booksReadFailed: books.booksReadFailed,
              }),
            ]),
          ]),
          // The owner is told the one next step this page can support, and the
          // account manager is told who does it — never to type or confirm
          // (rd round 11, R7-11).
          h("p", { class: "muted", "data-role": "mismatch-next" }, [
            ownerOnly
              ? "The owner checks the statement and the bank line, then confirms it."
              : MISMATCH_NEXT_STEP_COPY,
          ]),
          // A card's day can be wrong in a way Confirm cannot fix: a row
          // uploaded before the closing-date field existed carries no entered
          // date, so Books used the bank's own statement date. Say the one
          // supported correction, in words (fix round 4, LOW 2). A bank account
          // has no closing day to correct, so it is never told this.
          ...(closingDateAdvice === null
            ? []
            : [
                h(
                  "p",
                  { class: "muted", "data-role": "mismatch-closing-date-advice" },
                  [closingDateAdvice],
                ),
              ]),
          entriesLink,
          // The sentence the empty press answers with is on the card too, so
          // the box's emptiness is explained before the press (item 2).
          ...(ownerOnly
            ? []
            : [
                h(
                  "p",
                  { class: "muted", id: hintId, "data-role": "mismatch-hint" },
                  [MISMATCH_EMPTY_CONFIRM_COPY],
                ),
              ]),
        );
      } else if (row.statementBalanceDecimal !== null) {
        figures.push(
          h("p", { "data-role": "review-figure" }, [
            `Balance read from the statement: ${moneyText(row.statementBalanceDecimal, { fallback: "none read" })}.`,
          ]),
        );
      } else {
        figures.push(
          h("p", { class: "muted", id: hintId, "data-role": "review-figure" }, [
            ownerOnly
              ? `No balance was read from the statement. ${advice("confirm_statement", role)}`
              : "No balance was read from the statement. Enter the ending balance from the statement.",
          ]),
        );
      }
    }
    // rd13 L4 item 5: an account with its own closing day is labelled with the
    // date the statement really closes, so nobody reads the month as a
    // month-end comparison. The date comes from the row the Worker built
    // (`pipeline/plaid_reconcile.py:resolve_closing_date`: the card's own
    // statement date from the bank, else the ledger's configured cycle day,
    // else month end); the console invents none. Only a bound statement has a
    // closing date this card may print. An unbound or Plaid-only row with an
    // unknown cycle day carries the 1st of the month as a placeholder
    // (`pipeline/plaid_reconcile.py`), which is not a date any statement closed
    // on (rd13 L4 review round 1) — the settled-month surface above
    // (`renderPublicationStatus`) prints the day for a month Books checked.
    const closingDateLine: Node[] =
      !unbound && showsClosingDate(row.closingDate, row.period)
      ? [
          h("p", { class: "muted", "data-role": "confirm-closing-date" }, [
            `Closing date ${dateText(row.closingDate)}.`,
          ]),
        ]
      : [];
    const card = h(
      "div",
      {
        class: "statement-confirm-card card",
        "data-role": "statement-confirm-card",
        "data-recon-id": row.id,
        "data-outcome": row.outcome,
      },
      ownerOnly
        ? [
            h("h3", { class: "section-title" }, [
              unbound
                ? unboundHeading
                : row.outcome === "mismatch"
                  ? "Mismatch"
                  : "Waiting for the owner",
            ]),
            h("p", { class: "muted", "data-role": "confirm-account" }, [
              accountLabelBeside(row.account, offeredAccounts(), companies),
            ]),
            ...detailsWithFileCheck(row, fingerprint),
            h("p", { class: "muted" }, [formatPeriodLabel(row.period)]),
            ...closingDateLine,
            ...statementFileLine,
            ...figures,
            // A row with no binding cannot be confirmed by the owner either, so
            // the account manager is not sent to ask for something impossible.
            ...(unbound
              ? [
                  h(
                    "p",
                    { class: "status", "data-role": "confirm-unbound" },
                    [unboundCopy],
                  ),
                ]
              : [
                  h("p", { "data-role": "confirm-owner-only" }, [
                    advice("confirm_statement", role),
                  ]),
                ]),
          ]
        : [
        h("h3", { class: "section-title" }, [
          unbound
            ? unboundHeading
            : row.outcome === "mismatch"
              ? "Mismatch — confirm carefully"
              : "Confirm account + balance",
        ]),
        h("p", { class: "muted", "data-role": "confirm-account" }, [
          accountLabelBeside(row.account, offeredAccounts(), companies),
        ]),
        ...detailsWithFileCheck(row, fingerprint),
        h("p", { class: "muted" }, [formatPeriodLabel(row.period)]),
        ...closingDateLine,
        ...statementFileLine,
        ...figures,
        ...(changedNotice === null ? [] : [changedNotice]),
        // A confirmed card shows the sentence and no second press: the balance
        // is already stored, and a repeat press can only reuse it (rd round 11,
        // R7-07 and R7-22a).
        ...(unbound
          ? [
              h(
                "p",
                {
                  class: "status",
                  "data-role": "confirm-unbound",
                },
                [unboundCopy],
              ),
            ]
          : [
              h("label", { class: "file-label field" }, [
                h("span", { class: "field-label" }, ["Last 4 digits"]),
                maskInput,
              ]),
              h("label", { class: "file-label field" }, [
                h("span", { class: "field-label" }, ["Ending balance"]),
                balanceInput,
              ]),
              h("label", { class: "file-label field" }, [
                h("span", { class: "field-label" }, ["Words beside the last 4 digits"]),
                markerInput,
              ]),
              h("label", { class: "file-label field" }, [
                h("span", { class: "field-label" }, ["Words that mark the ending balance"]),
                cueInput,
              ]),
              confirmBtn,
              lockedLine,
              cardStatus,
            ]),
      ],
    );

    const state: ConfirmCardState = {
      row,
      element: card,
      signature: rowSignature(row),
      // A rebuilt card that carried typed values across is still the owner's
      // unsaved state, so a poll must keep treating it as dirty.
      dirty: carried !== null,
      busy: false,
      done: false,
      doneMessage: null,
      period: row.period,
      awaitingStoredFigure: false,
      locked,
      confirmedFingerprint: options.confirmedFingerprint ?? null,
      status: cardStatus,
      pressEpoch: 0,
    };
    /**
     * Lock the card to a figure a READ proved the server stored (round 3,
     * MUST-FIX 3). Only ever called with values taken from the row, never from
     * what was typed: "Saved at $X" may name only a figure a read handed back.
     */
    const lockAt = (values: TypedConfirmFields): void => {
      locked = values;
      state.locked = values;
      maskInput.value = values.mask;
      balanceInput.value = values.balance;
      markerInput.value = values.marker;
      cueInput.value = values.cue;
      for (const input of [maskInput, balanceInput, markerInput, cueInput]) {
        input.readOnly = true;
      }
      lockedLine.textContent = `Saved at ${moneyText(values.balance, { fallback: values.balance })}. It can't be changed here.`;
    };
    if (locked !== null) {
      lockAt(locked);
    }
    // Any edit is the owner's state, not the server's: this card is reused
    // across poll paints so the text survives (rd round 11, R7-09).
    const markDirty = (): void => {
      state.dirty = true;
    };
    for (const input of [maskInput, balanceInput, markerInput, cueInput]) {
      input.addEventListener("input", markDirty);
    }

    /** The typed values, or the ones a read already proved. */
    const confirmationValues = (): TypedConfirmFields => ({
      mask: locked?.mask ?? maskInput.value.trim(),
      balance: locked?.balance ?? balanceInput.value.trim(),
      marker: locked?.marker ?? markerInput.value.trim(),
      cue: locked?.cue ?? cueInput.value.trim(),
    });

    /**
     * The one gate every submit path calls (item B4b-7): the marker, the cue
     * and the balance, in one function, so a click and any Enter or shortcut
     * path refuse with the same words and nothing is sent twice.
     */
    const confirmationGate = (): string | null => {
      clear(cardStatus);
      const values = confirmationValues();
      // An empty figure is its own answer (rd13 L4 item 2): on a mismatch the
      // press is available while the box is empty, and it must say what is
      // missing rather than complain about the decimal format of nothing.
      if (values.balance === "") {
        cardStatus.className = "status banner-err";
        return MISMATCH_EMPTY_CONFIRM_COPY;
      }
      if (!MASK_PATTERN.test(values.mask)) {
        cardStatus.className = "status banner-err";
        return "Enter exactly 4 digits for the account mask.";
      }
      if (!/^-?(0|[1-9]\d*)(\.\d{1,2})?$/.test(values.balance)) {
        cardStatus.className = "status banner-err";
        return "Enter a valid decimal ending balance.";
      }
      if (values.marker.length < 1 || values.cue.length < 1) {
        cardStatus.className = "status banner-err";
        return "Marker pattern and balance cue are required.";
      }
      return null;
    };

    confirmBtn.addEventListener("click", () => {
      void (async () => {
        if (!stillCurrent()) return;
        /**
         * True while this press still owns the card. A repaint that resets the
         * card's status line has declared the press over (round 4), so a verdict
         * that arrives afterwards belongs to a card that is no longer on screen
         * and must not be written to it.
         */
        const pressEpoch = state.pressEpoch;
        const pressStillOwnsCard = (): boolean => state.pressEpoch === pressEpoch;
        syncConfirm();
        /**
         * End the press. The final line is written BEFORE `busy` is cleared, so
         * no path can leave "Checking whether it saved…" on a card whose Confirm
         * is live again (round 4, MUST-FIX 1).
         */
        const endPress = (line: string, className: string): void => {
          cardStatus.className = className;
          cardStatus.textContent = line;
          confirming = false;
          state.busy = false;
          syncConfirm();
        };
        // Confirming publishes a balance, so the books figure read before the
        // press is not the month's any more (round-3 MUST-FIX 1). Cache
        // invalidation stays OUT of `afterLanded`, which a failed re-read may
        // skip (carryover).
        forgetBooksRead();
        /**
         * One write (item B4b-7): `runWrite` owns the flight, the disabled
         * controls and the gate. It is given NO `readBack`: the page's own
         * deciding reads below are the fingerprint read-back, and they are
         * strictly more careful than a landed/not-landed answer. A landed
         * verdict here would paint the done card over a confirm whose workflow
         * dispatch failed, which is the one state the owner must be able to
         * press again to finish (rd Z8 round 2, MUST-FIX 2).
         */
        const outcome = await runWrite({
          key: "statement-confirm:" + row.id,
          scope: state.element,
          gate: confirmationGate,
          live: cardStatus,
          send: () =>
            client.confirmStatementReconciliation(
              company,
              row.id,
              statementConfirmBody(row, confirmationValues()),
            ),
          afterLanded: async () => {
            // The sentence is built AFTER the read-back, from the figure the
            // Worker stored on the row — never from the typed text (round 2,
            // MUST-FIX 1). This provisional card carries no figure at all; the
            // refreshed row fills it in. It names the fingerprint this press
            // confirmed, so the next read decides whether the done card may
            // stand (round 3, MUST-FIX 2).
            state.done = true;
            state.busy = false;
            state.awaitingStoredFigure = true;
            state.confirmedFingerprint = rowFingerprint(row);
            state.doneMessage = "Confirmed the ending balance.";
            const provisional = renderDoneConfirmCard(
              row,
              state.doneMessage,
              state.confirmedFingerprint,
            );
            provisional.awaitingStoredFigure = true;
            confirmCards.set(row.id, provisional);
            if (state.element.parentNode !== null) {
              state.element.replaceWith(provisional.element);
            }
            await refreshRecons();
          },
          stillCurrent,
        });
        if (outcome.kind === "landed") return;
        // The gate refused: it has already said why, and nothing was sent.
        if (outcome.kind === "refused" && outcome.status === 0) return;
        if (!stillCurrent()) return;
        {
          const error = outcome.error;
          // A 4xx is an answer on its own: it has a sentence and a next step,
          // and nothing was written (rd Z8; decision 1 for an owner's 403).
          // The primitive calls a 409 a "conflict" and everything else a
          // refusal, so the page asks its own question — only a 5xx, a dropped
          // answer or a non-HTTP fault needs a read.
          if (error !== undefined && !confirmFailureNeedsRead(error)) {
            endPress(statementConfirmRecovery(error, role), "status banner-err");
            return;
          }
          // Not an answer. The Worker stores the balance before the queue
          // UPDATE, so the figure may be on file even though the response was
          // not — or there was no readable response at all. Until a read says
          // otherwise the card claims nothing and stays disabled (round 3,
          // MUST-FIX 3).
          confirming = true;
          state.busy = true;
          syncConfirm();
          const dispatchFailed =
            error instanceof AdminApiError &&
            confirmRefusalReason(error.body) === "dispatch_failed";
          cardStatus.className = "status";
          // The rd-z8-pinned dispatch_failed sentence is the Worker's own
          // attestation that the balance was stored; every other fault waits
          // for the read.
          cardStatus.textContent = dispatchFailed
            ? statementConfirmRecovery(error, role)
            : CONFIRM_CHECKING_SAVED_COPY;
          // A write that may have landed moves the books: read them again too.
          forgetBooksRead();
          /**
           * What the card says when no read answered the question. The card may
           * not say "Nothing was saved" — no read disproved it — and it may not
           * keep "Checking…" over a Confirm that is live again, so it names the
           * Refresh that answers.
           *
           * Round 5, SHOULD-FIX 2: the rd-z8-pinned `dispatch_failed` sentence
           * is the Worker's own attestation that the balance WAS stored, so it
           * is kept here even though no read came back. Replacing it with the
           * can't-check line would retract a fact the Worker stated.
           */
          const endUnresolved = (): void => {
            endPress(
              dispatchFailed ? statementConfirmRecovery(error, role) : CONFIRM_CHECK_FAILED_COPY,
              dispatchFailed ? "status" : "status banner-err",
            );
          };
          // The statement this press belonged to. A read that names a different
          // one has moved the question entirely (round 4, SHOULD-FIX 3).
          const sentFingerprint = rowFingerprint(row);
          // Two reads answer the same question. The paint of the list is started
          // second, so its answer is the NEWER observation and the verdict
          // prefers it (round 5, MUST-FIX 1): a write that lands between the two
          // reads must not be reported as "Nothing was saved" while the list
          // below shows the row saved.
          //
          // The read this press asks itself goes first so it keeps its place in
          // the read order every fixture is keyed on, and it is the fallback for
          // a paint that is refused, held or overtaken.
          const paintSeqBefore = paintSequence;
          const decidingPromise = readDecidingRows(row.period);
          // The repaint runs anyway, so the list shows whatever the write did.
          // Its promise is not awaited directly: a held or overtaken paint never
          // settles as the newest read, and waiting on it is exactly how the
          // card used to be stuck on "Checking…" (round 4, MUST-FIX 1).
          const paintPromise = refreshRecons();
          const decided = await decidingPromise;
          if (!stillCurrent()) return;
          // A read that failed, and an answer that never mentioned this
          // statement at all, are both "no answer to this press": a row that is
          // not in the answer cannot be a row that was shown to be unsaved
          // (rd Z8 round 4; round 5, SHOULD-FIX 3). Only the newer paint can
          // still settle it, and only by PROVING the save.
          const answered =
            decided !== null && decided.some((item) => item.id === row.id);
          // Decide from the NEWEST read: the paint when it lands, and this
          // press's own read only when the paint is refused or overtaken. The
          // wait is bounded so a paint that never comes back cannot keep the
          // card on "Checking…" after the press is over (round 5, MUST-FIX 1).
          const newest = await newestPaintedRows(paintSeqBefore, paintPromise);
          if (!stillCurrent()) return;
          if (!pressStillOwnsCard()) return;
          const decidedRows = answered ? (newest ?? decided) : newest;
          const fresh = decidedRows?.find((item) => item.id === row.id) ?? null;
          if (fresh !== null && rowFingerprint(fresh) !== sentFingerprint) {
            // The statement this card was typed against is gone. The answer is
            // not "nothing was saved", it is a different card: clear `busy`
            // first, then build it through cardFor, which carries no typed
            // value, no prefill and no lock across a fingerprint change
            // (round 3, MUST-FIX 1) and adds the "This card changed" notice
            // (round 4, SHOULD-FIX 3).
            confirming = false;
            state.busy = false;
            state.dirty = false;
            state.locked = null;
            clear(cardStatus);
            const rebuilt = cardFor(fresh);
            if (state.element.parentNode !== null) state.element.replaceWith(rebuilt);
            return;
          }
          if (confirmRowProvesSave(fresh, sentFingerprint)) {
            // Only a read may name the figure: the lock and "Saved at $X" both
            // come from the row the read handed back (round 3, MUST-FIX 3).
            const typed = confirmationValues();
            lockAt({
              mask: typed.mask,
              balance: fresh?.statementBalanceDecimal ?? typed.balance,
              marker: typed.marker,
              cue: typed.cue,
            });
            if (!dispatchFailed) {
              endPress(CONFIRM_SAVED_PROVEN_COPY, "status");
            } else {
              confirming = false;
              state.busy = false;
              syncConfirm();
            }
            return;
          }
          if (!answered || fresh === null) {
            // No read answered about this statement, or the newest answer does
            // not name it. That is not proof that nothing was stored — it is
            // proof that the page cannot check (round 5, SHOULD-FIX 3).
            endUnresolved();
            return;
          }
          if (dispatchFailed) {
            // The rd-z8 sentence stands and nothing is locked, because this read
            // did not prove the save (rd Z8, round 3 MUST-FIX 3).
            confirming = false;
            state.busy = false;
            syncConfirm();
            return;
          }
          endPress(CONFIRM_NOTHING_SAVED_COPY, "status banner-err");
        }
      })();
    });

    return state;
  }

  /**
   * A finished Confirm, as a compact sentence-only card (round 2, MUST-FIX 3).
   * It has no fields and no Confirm press, and paintConfirmations places it
   * outside the "Needs confirmation" heading.
   */
  function renderDoneConfirmCard(
    row: StatementReconRow,
    message: string,
    confirmedFingerprint: string | null,
  ): ConfirmCardState {
    const card = h(
      "div",
      {
        class: "statement-confirm-card card",
        "data-role": "statement-confirm-card",
        "data-state": "done",
        "data-recon-id": row.id,
        "data-outcome": row.outcome,
      },
      [
        h("p", { class: "muted", "data-role": "confirm-account" }, [
          accountLabelBeside(row.account, offeredAccounts(), companies),
        ]),
        h("p", { class: "muted", "data-role": "confirm-period" }, [
          formatPeriodLabel(row.period),
        ]),
        h("p", { class: "status", role: "status", "data-role": "confirm-status" }, [
          message,
        ]),
      ],
    );
    return {
      row,
      element: card,
      signature: rowSignature(row),
      dirty: false,
      busy: false,
      done: true,
      doneMessage: message,
      period: row.period,
      awaitingStoredFigure: false,
      locked: null,
      confirmedFingerprint,
      // A done card's status is its finished sentence, not a line about a
      // press in flight: it is never reset.
      status: null,
      pressEpoch: 0,
    };
  }

  /**
   * What happens next to a balance that is confirmed but not yet in the books.
   *
   * A confirmation whose workflow dispatch failed is accepted (the balance is
   * saved) and the retry is allowed, but the retry body must resend the exact
   * mask, marker and cue the confirmation learned — values no read hands back,
   * so after a reload this page cannot build that body. The card therefore
   * promises no press it cannot offer (rd Z8 round 2, MUST-FIX 2) and says what
   * really finishes it: the statements run writes the assertion. Round 3
   * softened it to what is always true (MUST-FIX 2): the daily run is skipped
   * when pipelines are off and cannot write in a locked period, so the card may
   * not promise that run, and it is not the owner's place to be told there is
   * nothing to do. On the card that just failed the press, the Confirm control
   * is still on screen and the refusal sentence names it.
   */
  const publicationNextStep = (row: StatementReconRow): Node[] =>
    row.publicationState === "awaiting_publication"
      ? [
          h("p", { class: "muted", "data-role": "publication-next" }, [
            "The books should update on their own shortly.",
          ]),
        ]
      : [];

  function renderPublicationStatus(row: StatementReconRow): HTMLElement {
    const label =
      row.publicationState === "published"
        ? "Saved to the books."
        : row.publicationState === "awaiting_publication"
          ? "Not saved to the books yet. This balance is calculated, not confirmed."
          : "Calculated only. Nothing is saved to the books yet.";
    // The day Books checked the month against, where it is not simply the
    // month's last day — the same fact the confirm card prints, on the row the
    // owner/AM reads once a month has settled (rd14 T2, fix round 2). A card
    // whose closing day came from the bank's own statement date says so here
    // instead of through a review card that could never be cleared.
    const closingDateLine: Node[] = showsClosingDate(row.closingDate, row.period)
      ? [
          h("p", { class: "muted", "data-role": "publication-closing-date" }, [
            `Closing date ${dateText(row.closingDate)}.`,
          ]),
        ]
      : [];
    return h(
      "div",
      {
        class: "statement-publication-status card",
        "data-role": "statement-publication-status",
        "data-recon-id": row.id,
        "data-publication-state": row.publicationState,
      },
      [
        h("p", { class: "muted", "data-role": "publication-account" }, [
          accountLabelBeside(row.account, offeredAccounts(), companies),
        ]),
        h("p", { "data-role": "publication-label" }, [label]),
        ...closingDateLine,
        ...publicationNextStep(row),
        disclosure("Show details", h("p", {}, [row.account])),
      ],
    );
  }

  uploadBtn.addEventListener("click", () => {
    void (async () => {
      if (!stillCurrent()) return;
      clear(status);
      const file = fileInput.files?.[0];
      if (!file) {
        status.append(document.createTextNode("Choose a PDF file to upload."));
        return;
      }
      if (!isValidPeriod(period)) {
        status.append(document.createTextNode("Pick a valid period."));
        return;
      }
      const eligibleNow = chartLoad
        ? statementEligibleAccounts(
            chartLoad.snapshot.selectableAccounts,
            accountKinds,
            statementAccountNames(),
          )
        : [];
      const account = accountChooser?.getValue() ?? "";
      if (
        account.length === 0 ||
        !eligibleNow.includes(account) ||
        !ACCOUNT_PATTERN.test(account) ||
        account.length > 200
      ) {
        status.append(
          document.createTextNode(noAccountChosenCopy(chartLoad, role, accountKinds)),
        );
        return;
      }

      const recovery = clientSideRecovery(file);
      if (recovery) {
        status.className = "status banner-err";
        status.append(document.createTextNode(recovery));
        return;
      }

      // The closing date printed on the statement, for a card only. A card
      // without one is refused here, with the plain sentence, before anything
      // is sent (the Worker refuses it too — this is the readable half of the
      // same rule).
      const closingDateText = statementAccountIsCreditCard(account)
        ? closingDateInput.value
        : "";
      const closingDateProblem = statementClosingDateProblem(
        account,
        closingDateText,
        period,
      );
      if (closingDateProblem !== null) {
        status.className = "status banner-err";
        status.append(document.createTextNode(closingDateProblem));
        return;
      }

      uploadBtn.setAttribute("disabled", "true");
      try {
        const result = await client.uploadStatement(
          company,
          period,
          account,
          file,
          closingDateText,
        );
        if (!stillCurrent()) return;
        recordSessionUpload({
          company: result.company,
          period: result.period,
          account: accountText(result.account),
          uploadedAt: result.uploadedAt,
          contentSha256Hex: result.contentSha256Hex,
        });
        status.className = "status";
        status.append(
          document.createTextNode(
            `Uploaded for ${labelFor(result.company)} · ${formatPeriodLabel(result.period)}.`,
          ),
        );
        fileInput.value = "";
        closingDateInput.value = "";
        paintSession();
        void pollUntilSettled({
          key: `${result.period}|${accountText(result.account)}`,
          account: result.account,
          period: result.period,
        });
      } catch (error) {
        // Law 2: raw server reason never shown; detail to logs only.
        console.error("statement upload rejected", error);
        if (!stillCurrent()) return;
        status.className = "status banner-err";
        status.append(document.createTextNode(humanUploadRecovery(error, file, role)));
      } finally {
        uploadBtn.removeAttribute("disabled");
      }
    })();
  });
}

/**
 * Why no account could be used for the upload. "No bank or loan accounts are on
 * file" is said only about a chart that was read and has none: while the chart
 * read is in flight, or after it failed, the list is unknown, not empty
 * (round eight sweep).
 */
export function noAccountChosenCopy(
  load: ChartLoadState | null,
  role: StaffRole,
  kinds?: ReadonlyMap<string, StatementAccountKind>,
): string {
  if (load === null) return "The account list is still loading. Try again in a moment.";
  if (load.status === "error") {
    return "The account list could not be loaded. Reload the page to try again.";
  }
  if (load.status === "absent") {
    // Both roles can open the books (owner: "Yes, AM can see everything"), so
    // the account manager is no longer sent to the owner for it (B4b-3).
    return `The account list has not been loaded yet. ${advice("open_books", role)}`;
  }
  if (statementEligibleAccounts(load.snapshot.selectableAccounts, kinds).length > 0) {
    return "Pick a listed bank or credit card account. An unlisted name cannot be submitted from this page.";
  }
  // Nothing opens a bank or loan account from the console, for either role.
  // The account manager used to be told to ask the owner to add one in the
  // same breath as being told that cannot be done (round-3 review, item 3).
  return "No bank or loan accounts are on file yet. New accounts can't be opened from the console yet. Loan statements attach from the loan card, not as free text here.";
}

/**
 * "Show details" for a reconciliation row: the stored account, plus the file
 * check of the statement the row is bound to (rd Z8 — the owner is vouching
 * for a specific file, so the exact one is nameable). A row with no binding
 * shows no file check; the card says what to do instead.
 */
function detailsWithFileCheck(
  row: StatementReconRow,
  fingerprint: string | null,
): Node[] {
  return [
    disclosure(
      "Show details",
      h("div", {}, [
        h("p", {}, [row.account]),
        ...(fingerprint === null
          ? []
          : [
              h(
                "p",
                {
                  class: "muted mono",
                  "data-role": "confirm-file-check",
                  title: fingerprint,
                },
                [`Statement file check ${shortFileCheck(fingerprint)}`],
              ),
            ]),
      ]),
    ),
  ];
}

/**
 * The fingerprint a reconciliation row is bound to, or `null` when it names no
 * file. A missing field and an empty string are the same state as `null`
 * (rd Z8 round 2, MUST-FIX 3): nothing this page may send.
 */
function rowFingerprint(row: Pick<StatementReconRow, "contentSha256Hex">): string | null {
  const value = row.contentSha256Hex;
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * Fields that change without changing one word of the card. `updatedAt` is
 * bumped by every write, including a poll-visible no-op, so hashing it would
 * rebuild cards forever. Everything else is compared (round 2, SHOULD-FIX 6).
 */
export const STATEMENT_SIGNATURE_IGNORED_KEYS: readonly string[] = ["updatedAt"];

export interface StatementCardSignatureInput {
  /** The books figure this card prints, or null when it prints none. */
  books: BooksFigure | null;
  /** A session upload for the same account and month, matched or not. */
  sessionUpload: SessionUpload | null;
  /** The session upload that matches this row's fingerprint exactly. */
  matchedUpload: SessionUpload | null;
}

/**
 * Hash the whole reconciliation row minus the explicit denylist, plus the
 * books figure and the session-upload evidence the card's words depend on
 * (round 2, SHOULD-FIX 6). Enumerating the fields we happen to know about
 * missed every new field; subtracting from the row cannot.
 */
export function statementCardSignature(
  row: StatementReconRow,
  input: StatementCardSignatureInput,
): string {
  const ignored = new Set(STATEMENT_SIGNATURE_IGNORED_KEYS);
  const record = row as unknown as Record<string, unknown>;
  const fields = Object.keys(record)
    .filter((key) => !ignored.has(key))
    .sort()
    .map((key) => [key, record[key]]);
  const upload = (entry: SessionUpload | null): [string, string] | null =>
    entry === null ? null : [entry.contentSha256Hex, entry.uploadedAt];
  return JSON.stringify({
    row: fields,
    books: input.books,
    upload: upload(input.sessionUpload),
    matched: upload(input.matchedUpload),
  });
}

/** When this session uploaded the exact file the row is bound to, if it did. */
function sessionUploadFor(row: StatementReconRow): SessionUpload | null {
  const fingerprint = rowFingerprint(row);
  if (fingerprint === null) return null;
  return (
    listSessionUploads().find(
      (entry) =>
        entry.contentSha256Hex === fingerprint &&
        entry.company === row.company &&
        entry.period === row.period &&
        entry.account === row.account,
    ) ?? null
  );
}

/**
 * A statement this session uploaded for this account and month, whatever the
 * row's binding now says. Every upload nulls the binding until the workflow
 * restamps it, so during that window the row names no file and cannot be
 * matched by fingerprint — the account and month are the only evidence a check
 * is running, and this is the one thing that licenses "still being checked"
 * (rd Z8 round 3, MUST-FIX 1).
 */
function sessionUploadForAccount(row: StatementReconRow): SessionUpload | null {
  return (
    listSessionUploads().find(
      (entry) =>
        entry.company === row.company &&
        entry.period === row.period &&
        entry.account === row.account,
    ) ?? null
  );
}

/**
 * One row of "Uploaded this session": which company, month and account, when,
 * and what its check is doing (rd16 U, C4 + C2).
 *
 * rd16 U, C4: the row drew TWO "Show details" controls side by side — one for
 * the raw account path, one for the file check — which read as two unrelated
 * disclosures. There is one now, and the account's plain name is on the line,
 * so the raw ledger path is not printed at all.
 *
 * rd16 U, C2: an uploaded statement that nothing has read yet said nothing
 * about the check. The row now says it is waiting for the daily check, or that
 * the check is late, from the upload's own timestamp.
 */
function renderSessionRow(
  entry: SessionUpload,
  dir: CompanyDirectory,
  now: Date,
  state: { rowsKnown: boolean; checked: boolean },
): HTMLLIElement {
  const time = formatSessionTime(entry.uploadedAt);
  const hash = h(
    "span",
    { class: "muted mono", "data-role": "session-hash", title: entry.contentSha256Hex },
    [shortFileCheck(entry.contentSha256Hex)],
  );
  // A statement the Worker has since read has no waiting/late sentence: the
  // review card below it is the answer (rd16 U review, item 1). And a month the
  // page holds no rows for claims nothing (review round 2, item 3).
  const checkState =
    state.rowsKnown && !state.checked
      ? statementCheckStateCopy(entry.uploadedAt, now)
      : null;
  return h("li", { class: "session-upload-row", "data-role": "session-upload-row" }, [
    h("span", { "data-role": "session-company" }, [
      face.company(entry.company, dir, "sentence"),
    ]),
    h("span", { "data-role": "session-period" }, [formatPeriodLabel(entry.period)]),
    h("span", { class: "muted", "data-role": "session-account" }, [
      accountFace(entry.account, face.accountLabelContext(dir)),
    ]),
    h("span", { class: "muted", "data-role": "session-time" }, [time]),
    ...(checkState === null
      ? []
      : [
          h("span", { class: "muted", "data-role": "session-check-state" }, [checkState]),
        ]),
    disclosure("Show details", h("p", {}, ["File check ", hash])),
  ]);
}

/** Client-side type/size gate — human recovery before the network. */
export function clientSideRecovery(file: File): string | null {
  const type = file.type.toLowerCase();
  if (type !== "" && type !== "application/pdf") {
    return recoveryWrongType();
  }
  if (file.size <= 0 || file.size > MAX_STATEMENT_PDF_BYTES) {
    return recoveryTooLarge();
  }
  if (type === "" && !file.name.toLowerCase().endsWith(".pdf")) {
    return recoveryWrongType();
  }
  return null;
}

/**
 * Map a server rejection to the human recovery line.
 * Never returns the raw server message (Law 2).
 *
 * The file's own two sentences are true in exactly two cases the client can
 * prove: a file that is not a PDF, and one over the size limit. Everything
 * else is the one home's wording (`failureCopy`, write context) — decision 1's
 * owner 403 ("You do not have access to do that.", never "Refresh the page"),
 * the account manager's "Ask the owner.", "did not save" only for a refusal the
 * server actually made, and "we could not confirm it saved" for a 5xx or a lost
 * response (round 11, R7-01; round 2, SHOULD-FIX 8). The drift test compares
 * this against `failureCopy(..., { context: "write" })` for every status.
 */
export function humanUploadRecovery(
  error: unknown,
  file: File,
  role: StaffRole = "owner",
): string {
  if (file.size > MAX_STATEMENT_PDF_BYTES) return recoveryTooLarge();
  if (error instanceof AdminApiError && error.status === 400) {
    // A closing date the Worker refused is not a bad PDF: the file may be
    // perfect. The Worker answers a generic 400 and names the field in
    // `details`; the sentence here is the plain one the person can act on
    // (rd14 T2 fix round 3).
    if (errorField(error) === "closing_date") return recoveryClosingDate();
    return recoveryWrongType();
  }
  // The Worker answers 404 "Company or period not found" — the month cannot
  // take an upload at all, and NOTHING was uploaded. The decided 404 write
  // sentence ("That is already done.") would be a false claim about state
  // (wave-a carryover, B4b).
  if (error instanceof AdminApiError && error.status === 404) {
    return "This month can't take an upload here. Refresh and choose the month again.";
  }
  return failureCopy(error, { role, context: "write" }).sentence;
}

function recoveryWrongType(): string {
  return "That didn't look like a readable PDF — try re-exporting the statement as a PDF and uploading again.";
}

/**
 * The Worker refused the closing date (generic 400 + `details.field`), so the
 * file is not the problem. Plain words, no ids, no raw server message.
 */
function recoveryClosingDate(): string {
  return "That closing date did not look like a day in the month you picked. Check the date printed on the statement and upload again.";
}

/** The `details.field` the Worker named on a refusal, when it named one. */
function errorField(error: AdminApiError): string | null {
  const body = error.body;
  if (body === null || typeof body !== "object") return null;
  const err = (body as { error?: unknown }).error;
  if (err === null || typeof err !== "object") return null;
  const details = (err as { details?: unknown }).details;
  if (details === null || typeof details !== "object") return null;
  const field = (details as { field?: unknown }).field;
  return typeof field === "string" ? field : null;
}

function recoveryTooLarge(): string {
  return `That file's too large — the limit is ${String(LIMIT_MB)} MB.`;
}

/** The first text node under an element, or a new leading one if it has none. */
function firstTextNode(element: HTMLElement): Text | null {
  for (const child of Array.from(element.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) return child as Text;
  }
  const created = document.createTextNode("");
  element.insertBefore(created, element.firstChild);
  return created;
}

function formatSessionTime(iso: string): string {
  const d = new Date(iso);
  // An unreadable stamp prints a plain fallback, never the raw ISO (B4b-4).
  if (Number.isNaN(d.getTime())) return "Date not known";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
